// WorkBriefGuidedFlow.js — guided Request → Promise draft → Handoff → Delivery → Review UX
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AcceptanceReviewSection from "./AcceptanceReviewSection";
import InitiativeGuidancePanel from "./InitiativeGuidancePanel";
import TaskDocumentPanel from "./TaskDocumentPanel";
import { TaskExpandButton } from "./TaskFocusWorkspace";
import { workspaceRegionHidden } from "./taskWorkspaceProjection";
import {
  PREPARE_TASK_FIRST,
  QUESTION_DEFER,
  QUESTION_DOES_NOT_APPLY,
  QUESTION_NOT_SURE,
  QUESTION_RECOMMEND,
  QUESTION_REVISE,
  QUESTION_SAVE_ANSWER,
  TASK_LABEL,
} from "./taskCopy";
import "./TaskFocusWorkspace.css";
import {
  engineeringAcknowledgeIntentContract,
  engineeringRejectIntentContract,
  extractIntentContractDraft,
  getFeaturePlan,
  getIntentContractVersion,
  getIntentContractVersionReadiness,
  productApproveIntentContract,
  resolveIntentContractQuestion,
  reviseIntentContract,
} from "./api";
import TaskSourcesPanel from "./TaskSourcesPanel";
import {
  CATEGORY_LABELS,
  ENGINEERING_REJECTION_REASON_CODES,
  SEVERITY_LABELS,
  appendHumanNoteToContent,
  appendHumanPromiseToContent,
  appendHumanQuestionToContent,
  classifyQuestionText,
  contractToContentBody,
  deriveSeverity,
  deriveStepperStatuses,
  humanizePlanStatus,
  humanizeReadinessBlockers,
  humanizeWarnings,
  isBlockedOnlyByOpenQuestions,
  isHumanAddedAssumptionId,
  isHumanAddedCriterionId,
  cleanFounderDisplayText,
  isHumanAddedQuestionId,
  isPlanPreparing,
  markPromiseNotRequiredInContent,
  originLabel,
  promiseSourceSentence,
  promiseText,
  readQuestionNotes,
  readStoredContractIds,
  removePromiseFromContent,
  replacePromiseInContent,
  sourceTrustLabel,
  writeQuestionNotes,
  writeStoredContractIds,
} from "./workBriefGuidedHelpers";
import {
  HANDOFF_COACH_ROLE_LABELS,
  deriveHandoffCoach,
  requirementBlockFromSnapshot,
} from "./handoffCoach";
import {
  buildHandoffPromisePresentations,
  deriveDeliveryBullets,
  derivePromiseDisplayTitle,
} from "./handoffDeliveryBreakdown";
import {
  founderQuestionCategoryLabel,
  parseClarificationNote,
  partitionHandoffQuestions,
} from "./handoffQuestionDisplay";
import { suggestClarificationExample } from "./handoffClarificationSuggest";
import {
  buildHandoffDeliverySummary,
  deriveHandoffAgreementStatus,
  suggestCoveragePromiseDrafts,
} from "./handoffAgreementStatus";
import { freezeReadyLabel, projectCustomerReadiness } from "./customerReadiness";
import "./WorkBriefGuidedFlow.css";

/**
 * The server's refusal to advance requirements it has recorded as contradictory.
 * Reachable even with the coach deferring correctly, because the snapshot arrives
 * after mount and a click can land before it does.
 */
const CONTRADICTION_ERROR_CODE = "INTENT_CONTRACT_CONTRADICTION_OPEN";

function contradictionRefusalMessage(err) {
  const base = String(
    err?.message ||
      "These requirements ask for incompatible things, so this cannot move forward yet"
  ).replace(/[.\s]+$/, "");
  return `${base}. See “Where this stands” above for the conflict and how to settle it.`;
}

/** Survives React Strict Mode remounts so auto-extract doesn't double-fire. */
const autoPrepareStartedKeys = new Set();

export function resetHandoffAutoPrepareForTests() {
  autoPrepareStartedKeys.clear();
}

function Stepper({ steps }) {
  return (
    <ol className="wbg-stepper" data-testid="guided-stepper">
      {steps.map((step, index) => (
        <li
          key={step.id}
          className={`wbg-step wbg-step--${step.status}`}
          data-testid={`guided-step-${step.id}`}
          data-status={step.status}
        >
          <span className="wbg-step-index">{index + 1}</span>
          <span className="wbg-step-body">
            <span className="wbg-step-label">{step.label}</span>
            <span className="wbg-step-status" data-testid={`guided-step-${step.id}-detail`}>
              {step.detail}
            </span>
          </span>
          {index < steps.length - 1 ? (
            <span className="wbg-step-arrow" aria-hidden="true">
              →
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/**
 * @param {{
 *   repoId?: string,
 *   workBriefId?: string,
 *   featureRequestText?: string,
 *   planStatus?: string,
 *   planError?: object|null,
 *   hasAcceptanceCriteria?: boolean,
 *   onRetryPlanning?: () => void,
 *   latestDeliveryReview?: object|null,
 *   onLatestReviewChange?: (review: object|null) => void,
 *   onAgreementStatusChange?: (status: object) => void,
 * }} props
 */
function WorkBriefGuidedFlow({
  repoId,
  workBriefId,
  featureRequestText,
  planStatus,
  planError = null,
  hasAcceptanceCriteria = false,
  onRetryPlanning,
  latestDeliveryReview = null,
  onLatestReviewChange,
  onAgreementStatusChange,
  layout = "inline",
  onRequestExpand = null,
  readOnly = false,
  onSaveStateChange = null,
  onEscapeBlockedChange = null,
  onEngineerStartPack = null,
  workspaceSection = null,
  onWorkspaceStateChange = null,
}) {
  const [productText, setProductText] = useState(featureRequestText || "");
  const requestSectionRef = useRef(null);
  const addPromiseRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);

  const initialStoredIds = readStoredContractIds(
    workBriefId,
    String(featureRequestText || "").trim()
  );
  const [contractId, setContractId] = useState(initialStoredIds?.contractId || null);
  const [versionId, setVersionId] = useState(initialStoredIds?.versionId || null);
  const [contract, setContract] = useState(null);
  // Reported by the guidance panel, which owns the fetch. Held here so local
  // guidance can defer to the server rather than contradict it.
  const [orchestrationSnapshot, setOrchestrationSnapshot] = useState(null);
  const [transientDraft, setTransientDraft] = useState(null);
  const [coverageSummary, setCoverageSummary] = useState(null);
  const [readinessEnvelope, setReadinessEnvelope] = useState(null);
  const [lastPolicyChecked, setLastPolicyChecked] = useState("strict");
  const [approvedWithWarnings, setApprovedWithWarnings] = useState(false);
  const [engineerStartPack, setEngineerStartPack] = useState(null);
  const [planRestoreDone, setPlanRestoreDone] = useState(!workBriefId);
  /** Scratch notes only — never sent to the server / never clears Strict. */
  const [questionNotes, setQuestionNotes] = useState({});
  const [newPromiseText, setNewPromiseText] = useState("");
  const [newNoteText, setNewNoteText] = useState("");
  const [newQuestionText, setNewQuestionText] = useState("");
  const [markNotRequiredFor, setMarkNotRequiredFor] = useState(null);
  const [markNotRequiredReason, setMarkNotRequiredReason] = useState("");
  const [editingPromiseId, setEditingPromiseId] = useState(null);
  const [editPromiseText, setEditPromiseText] = useState("");
  const [rejectReasonCode, setRejectReasonCode] = useState("scope_unclear");
  const [rejectComment, setRejectComment] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  /** noteKey → true when textarea was filled from a suggested example */
  const [suggestedExampleKeys, setSuggestedExampleKeys] = useState({});
  /** Coverage suggestion id → edited source text */
  const [suggestionEdits, setSuggestionEdits] = useState({});
  const [dismissedSuggestionIds, setDismissedSuggestionIds] = useState([]);
  const [editingSuggestionId, setEditingSuggestionId] = useState(null);
  /** Prevents double auto-extract (Strict Mode / effect re-runs) for the same request. */
  const autoPrepareKeyRef = useRef("");
  const [documentPanelOpen, setDocumentPanelOpen] = useState(false);
  const [documentWidth, setDocumentWidth] = useState(352);

  useEffect(() => {
    if (typeof onSaveStateChange !== "function") return;
    if (busy) onSaveStateChange("Saving…");
    else if (statusMessage) onSaveStateChange(statusMessage);
    else onSaveStateChange("Saved");
  }, [busy, statusMessage, onSaveStateChange]);

  useEffect(() => {
    if (typeof onEscapeBlockedChange !== "function") return;
    onEscapeBlockedChange(Boolean(markNotRequiredFor || showRejectForm));
  }, [markNotRequiredFor, showRejectForm, onEscapeBlockedChange]);

  useEffect(() => {
    setProductText(featureRequestText || "");
    setError(null);
    setErrorDetails(null);
    setStatusMessage(null);
    setReadinessEnvelope(null);
    setTransientDraft(null);
    setCoverageSummary(null);
    setLastPolicyChecked("strict");
    setApprovedWithWarnings(false);
    setQuestionNotes(readQuestionNotes(workBriefId));
    setSuggestionEdits({});
    setDismissedSuggestionIds([]);
    setEditingSuggestionId(null);

    const request = String(featureRequestText || "").trim();
    const stored = readStoredContractIds(workBriefId, request);
    if (stored) {
      setContractId(stored.contractId);
      setVersionId(stored.versionId);
      setContract(null);
    } else {
      // No draft for this request (first time, or request text changed).
      setContractId(null);
      setVersionId(null);
      setContract(null);
      autoPrepareKeyRef.current = "";
    }
  }, [workBriefId, featureRequestText]);

  const loadVersion = useCallback(async (id) => {
    if (!id) return null;
    const data = await getIntentContractVersion(id);
    const next = data?.contract || null;
    setContract(next);
    if (next?.contractId) setContractId(String(next.contractId));
    if (next?._id) setVersionId(String(next._id));
    if (
      next?.state === "product_approved" ||
      next?.state === "engineering_acknowledged"
    ) {
      const openQs = Array.isArray(next.unresolvedQuestions)
        ? next.unresolvedQuestions.filter(
            (q) => !q.status || q.status === "open" || q.status === "deferred"
          )
        : [];
      setApprovedWithWarnings(openQs.length > 0);
    }
    return next;
  }, []);

  useEffect(() => {
    if (!versionId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // Do not set global `busy` for loads — Strict Mode cancel can leave actions stuck disabled.
        await loadVersion(versionId);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Could not load this Task.");
          setErrorDetails(err?.code || null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [versionId, loadVersion]);

  useEffect(() => {
    if (!versionId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await getIntentContractVersionReadiness(versionId, "strict");
        if (cancelled) return;
        setLastPolicyChecked("strict");
        setReadinessEnvelope(data);
      } catch (err) {
        if (!cancelled && err?.readiness) {
          setLastPolicyChecked("strict");
          setReadinessEnvelope({ readiness: err.readiness });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [versionId]);

  useEffect(() => {
    if (!workBriefId) {
      setPlanRestoreDone(true);
      return undefined;
    }
    let cancelled = false;
    setPlanRestoreDone(false);
    (async () => {
      try {
        const plan = await getFeaturePlan(workBriefId);
        if (cancelled || !plan) return;
        const pack = plan.engineerStartPack || plan.plan?.engineerStartPack || null;
        setEngineerStartPack(pack);
        if (typeof onEngineerStartPack === "function") onEngineerStartPack(pack);
        const frozen = plan.frozenIntent || plan.plan?.frozenIntent;
        const deliveries = plan.handoffDeliveries || plan.plan?.handoffDeliveries || [];
        const restoreVersion =
          frozen?.versionId ||
          pack?.frozenVersionId ||
          deliveries.at(-1)?.versionId;
        const restoreContract =
          frozen?.contractId ||
          pack?.contractId ||
          deliveries.at(-1)?.contractId;
        if (restoreVersion && restoreContract) {
          writeStoredContractIds(workBriefId, {
            contractId: restoreContract,
            versionId: restoreVersion,
            requestText: featureRequestText,
          });
          setContractId(String(restoreContract));
          setVersionId(String(restoreVersion));
        }
      } catch {
        /* pack restore is optional */
      } finally {
        if (!cancelled) setPlanRestoreDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Parent often passes an inline onEngineerStartPack. Re-running on that identity would loop restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore is keyed by Task id and request text
  }, [workBriefId, featureRequestText]);

  const draftView = contract || transientDraft;
  const hasPersistedVersion = Boolean(versionId && contractId);
  const criteria = useMemo(
    () => (Array.isArray(draftView?.criteria) ? draftView.criteria : []),
    [draftView]
  );
  const openQuestions = useMemo(() => {
    const qs = Array.isArray(draftView?.unresolvedQuestions)
      ? draftView.unresolvedQuestions
      : [];
    return qs.filter(
      (q) => !q.status || q.status === "open" || q.status === "deferred"
    );
  }, [draftView]);

  const resolvedQuestions = useMemo(() => {
    const qs = Array.isArray(draftView?.unresolvedQuestions)
      ? draftView.unresolvedQuestions
      : [];
    return qs.filter(
      (q) =>
        q.status === "answered" ||
        q.status === "not_required" ||
        q.status === "resolved_in_successor"
    );
  }, [draftView]);

  const originByCriterionId = useMemo(() => {
    const map = {};
    const origins = coverageSummary?.criterionOrigins;
    if (Array.isArray(origins)) {
      for (const entry of origins) {
        if (entry?.criterionId) map[entry.criterionId] = entry.origin;
      }
    }
    for (const c of criteria) {
      if (isHumanAddedCriterionId(c.criterionId)) {
        map[c.criterionId] = "human_added";
      }
    }
    return map;
  }, [coverageSummary, criteria]);

  const humanNotes = useMemo(() => {
    const assumptions = Array.isArray(draftView?.assumptions)
      ? draftView.assumptions
      : [];
    return assumptions.filter((a) => isHumanAddedAssumptionId(a.assumptionId));
  }, [draftView]);

  const classifiedQuestions = useMemo(() => {
    const fromReadiness = readinessEnvelope?.readiness?.questionSummary?.questions;
    const byId = {};
    if (Array.isArray(fromReadiness)) {
      for (const q of fromReadiness) {
        if (q?.questionId) byId[q.questionId] = q;
      }
    }
    return openQuestions.map((q) => {
      const classified = byId[q.questionId] || {
        category: classifyQuestionText(q.text),
        severity: null,
      };
      const category = classified.category || classifyQuestionText(q.text);
      const severity = classified.severity || deriveSeverity(category);
      return { ...q, ...classified, category, severity };
    });
  }, [openQuestions, readinessEnvelope]);

  const readiness = readinessEnvelope?.readiness || null;
  const contractState =
    contract?.state || (draftView && hasPersistedVersion ? "draft" : null);
  const isApproved =
    contractState === "product_approved" ||
    contractState === "engineering_acknowledged";
  const hasEngineeringRejection = Boolean(contract?.engineeringRejection?.reasonCode);
  const planInfo = useMemo(
    () => humanizePlanStatus(planStatus, planError),
    [planStatus, planError]
  );
  const planPreparing = isPlanPreparing(planStatus);
  const extracting = busy === "extract";
  /** Tip content may change via successor revision on draft or approved/acked tips. */
  const canReviseTip =
    !readOnly &&
    hasPersistedVersion &&
    Boolean(contractState) &&
    contractState !== "superseded" &&
    !extracting;
  const hasEngAck =
    contractState === "engineering_acknowledged" ||
    Boolean(contract?.engineeringAcknowledgment);
  const hasPromises = criteria.length > 0;
  /**
   * Changes whenever something the snapshot depends on has moved, so the guidance
   * panel refetches instead of showing a stale next action after the customer has
   * just done it. Derived from the state rather than incremented by hand, so no
   * mutation path can forget to bump it.
   */
  const guidanceRefreshKey = `${contractState || "none"}:${
    contract?.version ?? 0
  }:${planStatus || "none"}:${criteria.length}`;
  /** Lock until a loaded draft has ≥1 promise (also covers draft still loading). */
  const approvalLockedNoPromises = !hasPromises && !isApproved;
  /**
   * The server's ruling on the requirements themselves. Derived here, above every
   * local verdict on this screen, because each of them has to defer to it.
   */
  const requirementBlock = useMemo(
    () => requirementBlockFromSnapshot(orchestrationSnapshot),
    [orchestrationSnapshot]
  );
  const agreementStatus = useMemo(
    () =>
      deriveHandoffAgreementStatus({
        preparing: extracting && !draftView,
        hasDraft: Boolean(draftView),
        promiseCount: criteria.length,
        contractState,
        hasEngAck,
        hasEngineeringRejection,
        openQuestionCount: openQuestions.length,
        requirementsConflict: Boolean(requirementBlock),
      }),
    [
      extracting,
      draftView,
      criteria.length,
      contractState,
      hasEngAck,
      hasEngineeringRejection,
      openQuestions.length,
      requirementBlock,
    ]
  );

  const coverageSuggestions = useMemo(() => {
    const base = suggestCoveragePromiseDrafts({
      requestText: String(productText || featureRequestText || ""),
      existingPromiseTexts: criteria.map((c) => promiseText(c)),
    });
    const dismissed = new Set(dismissedSuggestionIds);
    return base
      .filter((s) => !dismissed.has(s.id))
      .map((s) => {
        const sourceText = suggestionEdits[s.id] || s.suggestedPromise;
        return {
          ...s,
          sourceText,
          title: derivePromiseDisplayTitle(sourceText) || s.area,
          deliveryBullets: deriveDeliveryBullets(sourceText),
        };
      });
  }, [
    productText,
    featureRequestText,
    criteria,
    dismissedSuggestionIds,
    suggestionEdits,
  ]);

  const handleAddSuggestionToHandoff = async (suggestion) => {
    const text = String(suggestion?.sourceText || "").trim();
    if (!text) {
      setError("Edit the suggested promise before adding it.");
      return;
    }
    const base = contractToContentBody(contract || draftView);
    if (!base) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    try {
      const nextContent = appendHumanPromiseToContent(base, text);
      await applyRevision(
        nextContent,
        {
          reason: "human_added_promise",
          note: `Added suggested ${suggestion.area || "coverage"} promise`,
        },
        `Added “${suggestion.area || "suggested"}” promise to the handoff.`
      );
      setDismissedSuggestionIds((prev) =>
        prev.includes(suggestion.id) ? prev : [...prev, suggestion.id]
      );
      setEditingSuggestionId(null);
    } catch (err) {
      setError(err?.message || "Could not add the suggested promise.");
    }
  };
  const deliveryBranch = latestDeliveryReview?.reviewSource?.branchName || "";
  const hasAcceptanceReview = Boolean(
    latestDeliveryReview?._id || latestDeliveryReview?.id
  );
  const customerReadiness = projectCustomerReadiness({
    planStatus,
    requestText: String(productText || featureRequestText || ""),
    workBriefId,
    hasDraft: Boolean(draftView),
    openQuestionCount: openQuestions.length,
    approvalAllowed: readiness?.approvalAllowed,
    policyMode: readiness?.policyMode || lastPolicyChecked,
    citationReady: readiness?.ready === true,
    contractState,
    hasEngAck,
    hasDeliveryBranch: Boolean(deliveryBranch),
    hasAcceptanceReview,
  });
  const approvalPolicyLabel = (() => {
    if (!isApproved && !hasEngineeringRejection) return null;
    if (approvedWithWarnings || openQuestions.length > 0) {
      return "Advisory (approved with warnings)";
    }
    return "Advisory";
  })();

  const questionsSectionRef = useRef(null);
  const handoffSectionRef = useRef(null);
  const deliverySectionRef = useRef(null);
  const acceptanceSectionRef = useRef(null);
  const guidanceSectionRef = useRef(null);

  const focusAddPromise = () => {
    addPromiseRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const input = addPromiseRef.current?.querySelector?.(
      "[data-testid='guided-new-promise-text']"
    );
    if (input && typeof input.focus === "function") input.focus();
  };

  const focusRequest = () => {
    requestSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const input = requestSectionRef.current?.querySelector?.(
      "[data-testid='guided-request-input']"
    );
    if (input && typeof input.focus === "function") input.focus();
  };

  const focusQuestions = () => {
    questionsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const focusHandoff = () => {
    handoffSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const focusDelivery = () => {
    deliverySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const focusAcceptance = () => {
    acceptanceSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const focusGuidance = () => {
    guidanceSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const steps = useMemo(
    () =>
      deriveStepperStatuses({
        hasRequest: Boolean(workBriefId),
        hasDraft: Boolean(draftView),
        promiseCount: criteria.length,
        openQuestionCount: openQuestions.length,
        contractState,
        approvalAllowed: readiness ? readiness.approvalAllowed : null,
        policyMode: readiness?.policyMode || lastPolicyChecked,
        hasEngAck,
        hasDeliveryBranch: Boolean(deliveryBranch),
        hasAcceptanceReview,
        planStatus,
        hasEngineeringRejection,
        hasAcceptanceCriteria,
        requirementsConflict: Boolean(requirementBlock),
      }),
    [
      workBriefId,
      draftView,
      criteria.length,
      openQuestions.length,
      contractState,
      readiness,
      lastPolicyChecked,
      hasEngAck,
      deliveryBranch,
      hasAcceptanceReview,
      planStatus,
      hasEngineeringRejection,
      hasAcceptanceCriteria,
      requirementBlock,
    ]
  );

  const blockers = humanizeReadinessBlockers(readiness);
  const warningLines = humanizeWarnings(readiness);
  const strictBlockedByQuestions =
    (readiness?.policyMode === "strict" || lastPolicyChecked === "strict") &&
    isBlockedOnlyByOpenQuestions(readiness);

  const rejectionReasonLabel = hasEngineeringRejection
    ? ENGINEERING_REJECTION_REASON_CODES.find(
        (r) => r.code === contract?.engineeringRejection?.reasonCode
      )?.label || contract?.engineeringRejection?.reasonCode
    : null;

  const promisePresentations = useMemo(
    () => buildHandoffPromisePresentations(draftView, criteria, openQuestions),
    [draftView, criteria, openQuestions]
  );
  const presentationById = useMemo(() => {
    const map = {};
    for (const p of promisePresentations.presentations || []) {
      if (p.criterionId) map[p.criterionId] = p;
    }
    return map;
  }, [promisePresentations]);

  const questionPartition = useMemo(
    () =>
      partitionHandoffQuestions({
        serverQuestions: classifiedQuestions,
        guidanceQuestions: promisePresentations.guidanceQuestions || [],
        requestText: String(productText || featureRequestText || ""),
        keepTechnicalFallbackInFounderList: false,
        clarificationNotes: humanNotes,
      }),
    [
      classifiedQuestions,
      promisePresentations,
      productText,
      featureRequestText,
      humanNotes,
    ]
  );

  const handoffDeliverySummary = useMemo(() => {
    const clarifications = [];
    const notes = [];
    for (const n of humanNotes) {
      const parsed = parseClarificationNote(n.text);
      if (parsed) {
        clarifications.push({
          question: cleanFounderDisplayText(parsed.question),
          answer: cleanFounderDisplayText(parsed.response),
          kind: parsed.kind,
        });
      } else {
        const t = cleanFounderDisplayText(n.text);
        if (t) notes.push(t);
      }
    }
    if (clarifications.length === 0) {
      for (const q of questionPartition.savedGuidanceQuestions || []) {
        clarifications.push({
          question: cleanFounderDisplayText(q.displayText),
          answer: cleanFounderDisplayText(q.savedResponse),
          kind: q.savedKind === "not_required" ? "not_required" : "answered",
        });
      }
    }
    const promises = criteria.map((c) =>
      cleanFounderDisplayText(
        promiseSourceSentence(c, draftView) || promiseText(c)
      )
    );
    return buildHandoffDeliverySummary({
      ready: agreementStatus.ready,
      state: contractState,
      contractId,
      versionId,
      promises,
      clarifications,
      notes,
      openQuestions: (questionPartition.founderQuestions || []).map((q) =>
        cleanFounderDisplayText(q.displayText)
      ),
    });
  }, [
    agreementStatus.ready,
    contractState,
    contractId,
    versionId,
    criteria,
    draftView,
    humanNotes,
    questionPartition,
  ]);

  useEffect(() => {
    if (typeof onAgreementStatusChange === "function") {
      onAgreementStatusChange({
        ...agreementStatus,
        deliverySummary: handoffDeliverySummary,
      });
    }
  }, [agreementStatus, handoffDeliverySummary, onAgreementStatusChange]);

  useEffect(() => {
    if (typeof onWorkspaceStateChange !== "function") return;
    onWorkspaceStateChange({
      contractState,
      hasEngAck,
      openQuestionCount: openQuestions.length,
      approvalAllowed: readiness?.approvalAllowed,
      intentFreezeReady: customerReadiness.intentFreezeReady,
      hasDraft: Boolean(draftView),
      promiseCount: criteria.length,
      frozenVersionNumber: contract?.version || null,
      orchestrationSnapshot,
    });
  }, [
    onWorkspaceStateChange,
    contractState,
    hasEngAck,
    openQuestions.length,
    readiness?.approvalAllowed,
    customerReadiness.intentFreezeReady,
    draftView,
    criteria.length,
    contract?.version,
    orchestrationSnapshot,
  ]);

  const coach = useMemo(
    () =>
      deriveHandoffCoach({
        requirementBlock,
        planStatus,
        planError,
        isExtracting: busy === "extract",
        hasRequest:
          Boolean(workBriefId) ||
          Boolean(String(productText || featureRequestText || "").trim()),
        requestText: String(productText || featureRequestText || ""),
        hasDraft: Boolean(draftView),
        promiseCount: criteria.length,
        promiseTexts: criteria.map((c) => promiseText(c)),
        openQuestionCount: openQuestions.length,
        noteCount: humanNotes.length,
        contractState,
        approvalAllowed: readiness ? readiness.approvalAllowed : null,
        policyMode: readiness?.policyMode || lastPolicyChecked,
        approvedWithWarnings,
        hasEngAck,
        hasEngineeringRejection,
        rejectionReasonLabel,
        hasDeliveryBranch: Boolean(deliveryBranch),
        hasAcceptanceReview,
        hasAcceptanceCriteria,
        canRetryPlanning: typeof onRetryPlanning === "function",
      }),
    [
      requirementBlock,
      planStatus,
      planError,
      busy,
      workBriefId,
      productText,
      featureRequestText,
      draftView,
      criteria,
      openQuestions.length,
      humanNotes.length,
      contractState,
      readiness,
      lastPolicyChecked,
      approvedWithWarnings,
      hasEngAck,
      hasEngineeringRejection,
      rejectionReasonLabel,
      deliveryBranch,
      hasAcceptanceReview,
      hasAcceptanceCriteria,
      onRetryPlanning,
    ]
  );

  const runCoachAction = (actionId) => {
    switch (actionId) {
      case "make_request_specific":
        focusRequest();
        break;
      case "prepare_handoff":
      case "try_again_from_request":
      case "create_promise_draft_anyway":
        handlePrepareHandoff();
        break;
      case "add_promise":
      case "revise_handoff":
        focusAddPromise();
        break;
      case "focus_questions":
        focusQuestions();
        break;
      case "approve_with_warnings":
        focusHandoff();
        handleApproveAdvisory();
        break;
      case "check_strict":
        focusHandoff();
        handleCheckStrict();
        break;
      case "eng_accept":
        focusDelivery();
        handleAck();
        break;
      case "eng_request_changes":
        focusDelivery();
        setShowRejectForm(true);
        break;
      case "select_branch":
      case "review_delivered_branch":
        focusAcceptance();
        break;
      case "retry_planning":
        if (typeof onRetryPlanning === "function") onRetryPlanning();
        break;
      case "resolve_requirement_conflict":
        focusGuidance();
        break;
      default:
        break;
    }
  };

  const applySuggestedExample = (noteKey, questionText) => {
    const suggestion = suggestClarificationExample({
      question: questionText,
      requestText: String(productText || featureRequestText || ""),
    });
    if (!suggestion?.example) {
      setError("Could not suggest an example for this question.");
      return;
    }
    updateQuestionNote(noteKey, suggestion.example);
    setSuggestedExampleKeys((prev) => ({ ...prev, [noteKey]: true }));
    setStatusMessage("Recommended option filled in — edit it, then save if you want it on the Task.");
  };

  const updateQuestionNote = (questionId, text) => {
    setQuestionNotes((prev) => {
      const next = { ...prev, [questionId]: text };
      writeQuestionNotes(workBriefId, next);
      return next;
    });
  };

  const canResolveQuestions =
    !readOnly &&
    hasPersistedVersion &&
    contractState &&
    contractState !== "superseded" &&
    !extracting;

  const applyQuestionResolutionResult = async (data, successMessage) => {
    const next = data?.contract || null;
    setContract(next);
    setTransientDraft(null);
    if (next?._id && next?.contractId) {
      const ids = {
        contractId: String(next.contractId),
        versionId: String(next._id),
        requestText: String(productText || featureRequestText || "").trim() || undefined,
      };
      setContractId(ids.contractId);
      setVersionId(ids.versionId);
      writeStoredContractIds(workBriefId, ids);
    }
    if (data?.readiness?.strict) {
      setLastPolicyChecked("strict");
      setReadinessEnvelope({
        readiness: data.readiness.strict,
        versionId: next?._id ? String(next._id) : versionId,
        contractId: next?.contractId ? String(next.contractId) : contractId,
      });
    } else if (next?._id) {
      try {
        const readinessData = await getIntentContractVersionReadiness(
          String(next._id),
          "strict"
        );
        setLastPolicyChecked("strict");
        setReadinessEnvelope(readinessData);
      } catch {
        /* optional */
      }
    }
    const successorNote = data?.successorCreated
      ? " A new draft version was created — re-approve if this handoff was already approved."
      : "";
    const discovery = data?.readiness?.strict?.questionSummary;
    const loopNote = discovery?.nextQuestion
      ? ` Next: ${discovery.nextQuestion.label || "the highest-value open decision"}.`
      : discovery && Number(discovery.materialCount) === 0
        ? " Discovery is complete: no material decisions remain."
        : "";
    setStatusMessage(`${successMessage}${loopNote}${successorNote}`);
    setApprovedWithWarnings(false);
  };

  const handleResolveQuestion = async (questionId, action, overrides = {}) => {
    if (!versionId) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    if (!canResolveQuestions) {
      setError("Questions can only be resolved on the current tip version.");
      return;
    }
    let draftText = String(questionNotes[questionId] || "").trim();
    if (action === "answer" && overrides.answerText != null) {
      draftText = String(overrides.answerText).trim();
    } else if (action === "mark_not_required" && overrides.reason != null) {
      draftText = String(overrides.reason).trim();
    } else if (action === "defer" && overrides.note != null) {
      draftText = String(overrides.note).trim();
    }
    const payload = { action };
    if (action === "answer") {
      if (!draftText) {
        setError("Enter an answer before saving.");
        return;
      }
      payload.answerText = draftText;
    } else if (action === "mark_not_required") {
      payload.reason = draftText || "This does not apply to this Task.";
    } else if (action === "defer") {
      if (draftText) payload.note = draftText;
    }

    setBusy(`resolve:${questionId}:${action}`);
    setError(null);
    setErrorDetails(null);
    try {
      const data = await resolveIntentContractQuestion(versionId, questionId, payload);
      const labels = {
        answer: "Answer saved on the server.",
        mark_not_required: "Marked not required on the server.",
        defer: "Question deferred. You can return to it later.",
      };
      await applyQuestionResolutionResult(data, labels[action] || "Question updated.");
      if (action === "answer" || action === "mark_not_required") {
        updateQuestionNote(questionId, "");
      }
    } catch (err) {
      setError(err?.message || "Could not resolve the question on the server.");
      setErrorDetails(err?.code || null);
    } finally {
      setBusy(null);
    }
  };

  /** Guidance clarifications are not server questions — persist as engineer notes. */
  const handleGuidanceQuestionNote = async (guidanceKey, questionText, action) => {
    const base = contractToContentBody(contract || draftView);
    if (!base) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    let draftText = String(questionNotes[guidanceKey] || "").trim();
    if (action === "answer" && !draftText) {
      setError("Enter an answer before saving.");
      return;
    }
    if (action === "mark_not_required" && !draftText) {
      draftText = "This does not apply to this Task.";
    }
    if (action === "defer") {
      setStatusMessage("Deferred — still shown as a clarification.");
      return;
    }
    const noteText =
      action === "answer"
        ? `Clarification — ${questionText} Answer: ${draftText}`
        : `Clarification not required — ${questionText} Reason: ${draftText}`;
    try {
      const nextContent = appendHumanNoteToContent(base, noteText);
      await applyRevision(
        nextContent,
        {
          reason: "human_clarification_note",
          note: action === "answer" ? "Clarification answered" : "Clarification not required",
        },
        action === "answer"
          ? "Answer saved. It’s listed under Saved clarifications and included for the engineer."
          : "Marked not required. It’s listed under Saved clarifications and included for the engineer."
      );
      updateQuestionNote(guidanceKey, "");
      setSuggestedExampleKeys((prev) => {
        const next = { ...prev };
        delete next[guidanceKey];
        return next;
      });
    } catch (err) {
      setError(err?.message || "Could not save the clarification note.");
    }
  };

  const applyRevision = async (nextContent, revisionMeta, successMessage) => {
    if (!versionId) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    if (!canReviseTip) {
      setError(
        extracting
          ? "Wait until promise extraction finishes."
          : "This handoff version cannot be changed."
      );
      return;
    }
    const needsReapproval = isApproved;
    setBusy("revise");
    setError(null);
    setErrorDetails(null);
    try {
      const data = await reviseIntentContract(versionId, nextContent, revisionMeta);
      const next = data?.contract || null;
      setContract(next);
      setTransientDraft(null);
      if (next?._id && next?.contractId) {
        const ids = {
          contractId: String(next.contractId),
          versionId: String(next._id),
          requestText: String(productText || featureRequestText || "").trim() || undefined,
        };
        setContractId(ids.contractId);
        setVersionId(ids.versionId);
        writeStoredContractIds(workBriefId, ids);
      }
      const reapprovalNote = needsReapproval
        ? " This changes the agreement, so it needs approval again."
        : "";
      setStatusMessage(`${successMessage}${reapprovalNote}`);
      setApprovedWithWarnings(false);
      setShowRejectForm(false);
      if (next?._id) {
        try {
          const readinessData = await getIntentContractVersionReadiness(
            String(next._id),
            "strict"
          );
          setLastPolicyChecked("strict");
          setReadinessEnvelope(readinessData);
        } catch {
          /* optional */
        }
      }
    } catch (err) {
      setError(err?.message || "Could not save your change to the handoff draft.");
      setErrorDetails(err?.code || null);
    } finally {
      setBusy(null);
    }
  };

  const handleRemovePromise = async (criterionId) => {
    const base = contractToContentBody(contract || draftView);
    if (!base) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    try {
      const nextContent = removePromiseFromContent(base, criterionId);
      await applyRevision(
        nextContent,
        { reason: "human_removed_promise", note: `Removed ${criterionId}` },
        "Promise removed from the handoff."
      );
    } catch (err) {
      setError(err?.message || "Could not remove promise.");
    }
  };

  const handleMarkPromiseNotRequired = async (criterionId) => {
    const base = contractToContentBody(contract || draftView);
    if (!base) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    try {
      const nextContent = markPromiseNotRequiredInContent(
        base,
        criterionId,
        markNotRequiredReason
      );
      await applyRevision(
        nextContent,
        {
          reason: "human_mark_promise_not_required",
          note: `Not required: ${criterionId}`,
        },
        "Promise marked not required and recorded for the engineer."
      );
      setMarkNotRequiredFor(null);
      setMarkNotRequiredReason("");
    } catch (err) {
      setError(err?.message || "Could not mark promise not required.");
    }
  };

  const handleSaveEditedPromise = async (criterionId) => {
    const base = contractToContentBody(contract || draftView);
    if (!base) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    try {
      const nextContent = replacePromiseInContent(base, criterionId, editPromiseText);
      await applyRevision(
        nextContent,
        { reason: "human_edited_promise", note: `Edited ${criterionId}` },
        "Promise updated on the handoff draft."
      );
      setEditingPromiseId(null);
      setEditPromiseText("");
    } catch (err) {
      setError(err?.message || "Could not edit promise.");
    }
  };

  const handleAddPromise = async () => {
    const base = contractToContentBody(contract || draftView);
    if (!base) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    try {
      const nextContent = appendHumanPromiseToContent(base, newPromiseText);
      await applyRevision(
        nextContent,
        { reason: "human_added_promise", note: "User added a promise" },
        "Promise added. Handoff decision unlocks once readiness allows."
      );
      setNewPromiseText("");
    } catch (err) {
      setError(err?.message || "Could not add promise.");
    }
  };

  const handleAddNote = async () => {
    const base = contractToContentBody(contract || draftView);
    if (!base) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    try {
      const nextContent = appendHumanNoteToContent(base, newNoteText);
      await applyRevision(
        nextContent,
        { reason: "human_added_note", note: "User added a note" },
        "Note added to the handoff draft."
      );
      setNewNoteText("");
    } catch (err) {
      setError(err?.message || "Could not add note.");
    }
  };

  const handleOfferOpenQuestion = async () => {
    const base = contractToContentBody(contract || draftView);
    if (!base) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    const text = String(newQuestionText || "").trim();
    if (!text) {
      setError("Enter the question you want product to answer.");
      return;
    }
    try {
      const nextContent = appendHumanQuestionToContent(base, text);
      await applyRevision(
        nextContent,
        { reason: "human_offered_question", note: "Engineer offered an open question" },
        isApproved
          ? "Open question added. This changes the agreement — product must approve again."
          : "Open question added for product to answer. It can block Strict until resolved."
      );
      setNewQuestionText("");
    } catch (err) {
      setError(err?.message || "Could not add the open question.");
    }
  };

  const handlePrepareHandoff = useCallback(async () => {
    setError(null);
    setErrorDetails(null);
    setStatusMessage(null);
    setReadinessEnvelope(null);
    setApprovedWithWarnings(false);
    if (!repoId) {
      setError("No repository selected.");
      return;
    }
    const request = String(productText || "").trim();
    if (!request && !workBriefId) {
      setError("Describe what you want, or add screenshots, first.");
      return;
    }
    try {
      setBusy("extract");
      const result = await extractIntentContractDraft({
        repoId,
        productRequestText: request,
        featurePlanId: workBriefId || undefined,
        persist: true,
      });
      setCoverageSummary(result.coverageSummary || null);
      setTransientDraft(result.draft || null);

      if (result.persisted?.versionId && result.persisted?.contractId) {
        const ids = {
          contractId: String(result.persisted.contractId),
          versionId: String(result.persisted.versionId),
          requestText: request,
        };
        setContractId(ids.contractId);
        setVersionId(ids.versionId);
        writeStoredContractIds(workBriefId, ids);
        const loaded = await loadVersion(ids.versionId);
        const loadedCount = Array.isArray(loaded?.criteria)
          ? loaded.criteria.length
          : Array.isArray(result.draft?.criteria)
            ? result.draft.criteria.length
            : 0;
        if (loadedCount === 0) {
          setStatusMessage(
            "No promises found yet. A handoff needs at least one promise before it can be approved."
          );
        } else {
          setStatusMessage(
            "Promise draft ready. Review promises, then approve the handoff when remaining decisions are settled."
          );
        }
        try {
          const readinessData = await getIntentContractVersionReadiness(
            ids.versionId,
            "strict"
          );
          setLastPolicyChecked("strict");
          setReadinessEnvelope(readinessData);
        } catch {
          /* optional */
        }
      } else {
        setStatusMessage("Promise draft extracted, but it was not saved. Try again.");
      }
    } catch (err) {
      setError(err?.message || "Could not prepare the handoff draft.");
      setErrorDetails(
        err?.code ||
          JSON.stringify(err?.originalError?.response?.data || {}, null, 2)
      );
      // Allow another auto-attempt after a failure for this request.
      autoPrepareKeyRef.current = "";
    } finally {
      setBusy(null);
    }
  }, [repoId, productText, workBriefId, loadVersion]);

  // Discovery starts from usable sources. Engineering-plan generation must not block it.
  useEffect(() => {
    const request = String(productText || featureRequestText || "").trim();
    if (!repoId || !workBriefId) return undefined;
    if (!planRestoreDone) return undefined;
    if (busy) return undefined;
    // Prefer any in-memory draft; also respect session binding for this request.
    if (draftView || versionId || contractId) return undefined;
    if (readStoredContractIds(workBriefId, request)) return undefined;

    const key = `${workBriefId}::${request || "__sources__"}`;
    if (autoPrepareKeyRef.current === key || autoPrepareStartedKeys.has(key)) {
      return undefined;
    }
    autoPrepareKeyRef.current = key;
    autoPrepareStartedKeys.add(key);
    handlePrepareHandoff().finally(() => {
      /* Keep key in the set on success so remounts don't re-extract.
         On failure handlePrepareHandoff clears autoPrepareKeyRef; also drop the set key. */
      if (!autoPrepareKeyRef.current) {
        autoPrepareStartedKeys.delete(key);
      }
    });
    return undefined;
  }, [
    repoId,
    workBriefId,
    productText,
    featureRequestText,
    busy,
    draftView,
    versionId,
    contractId,
    planRestoreDone,
    handlePrepareHandoff,
  ]);

  const handleCheckStrict = async () => {
    setError(null);
    setErrorDetails(null);
    setStatusMessage(null);
    if (!versionId) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    try {
      setBusy("readiness");
      setLastPolicyChecked("strict");
      const data = await getIntentContractVersionReadiness(versionId, "strict");
      setReadinessEnvelope(data);
      if (data?.readiness?.approvalAllowed) {
        setStatusMessage("Strict handoff looks ready.");
      } else {
        setStatusMessage("Strict handoff is blocked because questions remain.");
      }
    } catch (err) {
      setError(err?.message || "Could not check strict readiness.");
      setErrorDetails(err?.code || null);
      if (err?.readiness) setReadinessEnvelope({ readiness: err.readiness });
    } finally {
      setBusy(null);
    }
  };

  const handleApproveAdvisory = async () => {
    setError(null);
    setErrorDetails(null);
    setStatusMessage(null);
    if (!versionId) {
      setError(PREPARE_TASK_FIRST);
      return;
    }
    if (!hasPromises) {
      setError(
        "This is not ready to hand off until there is at least one promise. Add a promise or try again from the request."
      );
      return;
    }
    try {
      setBusy("approve");
      setLastPolicyChecked("strict");
      const data = await productApproveIntentContract(versionId, {
        policy: "strict",
      });
      const next = data?.contract || null;
      setContract(next);
      setApprovedWithWarnings(false);
      setStatusMessage("Handoff approved. Next: engineer acknowledges handoff.");
      if (data?.engineerStartPack) {
        setEngineerStartPack(data.engineerStartPack);
        if (typeof onEngineerStartPack === "function") onEngineerStartPack(data.engineerStartPack);
      }
      if (next?._id) await loadVersion(String(next._id));
      try {
        const readinessData = await getIntentContractVersionReadiness(
          versionId,
          "strict"
        );
        setReadinessEnvelope(readinessData);
      } catch {
        /* optional */
      }
    } catch (err) {
      const onlyQuestions = isBlockedOnlyByOpenQuestions(err?.readiness);
      const human =
        err?.code === CONTRADICTION_ERROR_CODE
          ? contradictionRefusalMessage(err)
          : err?.code === "INTENT_CONTRACT_NOT_READY"
          ? onlyQuestions
            ? "Approval is blocked until remaining material decisions are answered or explicitly deferred."
            : "Not ready to approve yet. See why below."
          : err?.message || "Could not approve handoff.";
      setError(human);
      setErrorDetails([err?.code, err?.message].filter(Boolean).join(": ") || null);
      if (err?.readiness) setReadinessEnvelope({ readiness: err.readiness });
    } finally {
      setBusy(null);
    }
  };

  const handleAck = async () => {
    setError(null);
    setErrorDetails(null);
    setStatusMessage(null);
    if (!versionId) {
      setError("Approve the handoff first.");
      return;
    }
    if (hasEngineeringRejection) {
      setError("Engineer requested changes. Revise the handoff and approve again first.");
      return;
    }
    try {
      setBusy("ack");
      const data = await engineeringAcknowledgeIntentContract(versionId);
      setContract(data?.contract || null);
      setStatusMessage("Engineer accepted the handoff.");
      if (data?.contract?._id) await loadVersion(String(data.contract._id));
    } catch (err) {
      setError(
        err?.code === CONTRADICTION_ERROR_CODE
          ? contradictionRefusalMessage(err)
          : err?.message || "Could not record engineer acknowledgment."
      );
      setErrorDetails(err?.code || null);
      if (err?.readiness) setReadinessEnvelope({ readiness: err.readiness });
    } finally {
      setBusy(null);
    }
  };

  const handleRequestChanges = async () => {
    setError(null);
    setErrorDetails(null);
    setStatusMessage(null);
    if (!versionId) {
      setError("Approve the handoff first.");
      return;
    }
    if (!String(rejectComment || "").trim()) {
      setError("Add a short comment explaining what needs to change.");
      return;
    }
    try {
      setBusy("reject");
      const data = await engineeringRejectIntentContract(versionId, {
        reasonCode: rejectReasonCode,
        comment: rejectComment,
      });
      setContract(data?.contract || null);
      setShowRejectForm(false);
      setStatusMessage(
        "Engineer requested changes. Revise the handoff (promises/notes/questions), then approve again."
      );
      if (data?.contract?._id) await loadVersion(String(data.contract._id));
    } catch (err) {
      setError(err?.message || "Could not record the change request.");
      setErrorDetails(err?.code || null);
    } finally {
      setBusy(null);
    }
  };

  const coachPrimaryDisabled = (() => {
    if (readOnly) return true;
    const id = coach.primaryAction?.id;
    if (!id) return true;
    if (id === "approve_with_warnings") {
      return (
        Boolean(busy) ||
        extracting ||
        !hasPersistedVersion ||
        approvalLockedNoPromises ||
        hasEngAck ||
        (isApproved && !hasEngineeringRejection) ||
        (hasEngineeringRejection && contractState === "product_approved")
      );
    }
    if (id === "eng_accept") {
      return (
        Boolean(busy) ||
        extracting ||
        !hasPersistedVersion ||
        contractState !== "product_approved" ||
        hasEngAck ||
        hasEngineeringRejection
      );
    }
    if (id === "eng_request_changes") {
      return (
        Boolean(busy) ||
        extracting ||
        !hasPersistedVersion ||
        contractState !== "product_approved" ||
        hasEngAck ||
        hasEngineeringRejection
      );
    }
    if (
      id === "prepare_handoff" ||
      id === "try_again_from_request" ||
      id === "create_promise_draft_anyway"
    ) {
      return Boolean(busy) || !repoId || isApproved;
    }
    if (id === "check_strict") {
      return Boolean(busy) || extracting || !hasPersistedVersion;
    }
    if (id === "retry_planning") {
      return Boolean(busy) || planPreparing || typeof onRetryPlanning !== "function";
    }
    return Boolean(busy);
  })();

  const documentPromises = (criteria || [])
    .map((c) => ({
      text: promiseText(c) || c?.text || "",
      fromAnswer: String(c?.criterionId || "").startsWith("crit-decision-"),
    }))
    .filter((item) => item.text);
  const documentQuestions = (questionPartition.founderQuestions || [])
    .map((q) => ({
      id: q.questionId,
      text: cleanFounderDisplayText(q.displayText),
      revealedByAnswer: /-followup-/i.test(String(q.questionId || "")),
    }))
    .filter((item) => item.text);
  const startDocumentResize = (event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = documentWidth;
    const onMove = (moveEvent) => {
      const next = Math.min(640, Math.max(260, startWidth + (startX - moveEvent.clientX)));
      setDocumentWidth(next);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <section
      className={`work-brief-guided-flow${
        layout === "focus" ? " wbg-layout-focus" : ""
      }${layout === "focus" && documentPanelOpen ? " wbg-document-open" : ""}${
        workspaceSection ? " wbg-workspace-section" : ""
      }`}
      data-testid="work-brief-guided-flow"
      data-layout={layout}
      data-workspace-section={workspaceSection || ""}
      data-discovery-may-begin={customerReadiness.discoveryMayBegin ? "true" : "false"}
      data-freeze-ready={customerReadiness.intentFreezeReady ? "true" : "false"}
      data-freeze-ready-label={freezeReadyLabel(customerReadiness)}
      style={
        layout === "focus" ? { "--task-doc-width": `${documentWidth}px` } : undefined
      }
    >
      <header className="wbg-header" hidden={Boolean(workspaceSection)}>
        <div className="wbg-header-top">
          <div>
            <h3>Feature Handoff</h3>
            <p className="wbg-lede">
              One workflow: request → promises → approve handoff → engineer accepts or asks for changes →
              review the delivered branch.
            </p>
          </div>
          {layout !== "focus" && typeof onRequestExpand === "function" ? (
            <TaskExpandButton onClick={onRequestExpand} testId="task-expand-in-flow" />
          ) : null}
          {layout === "focus" ? (
            <button
              type="button"
              className="wbg-document-toggle"
              data-testid="task-document-toggle"
              aria-expanded={documentPanelOpen}
              onClick={() => setDocumentPanelOpen((open) => !open)}
            >
              {documentPanelOpen ? `Hide ${TASK_LABEL} draft` : `${TASK_LABEL} draft`}
            </button>
          ) : null}
        </div>
        <Stepper steps={steps} />
      </header>

      <div className="wbg-layout-body">
        <aside
          className="wbg-region-sources"
          data-testid="task-focus-sources"
          hidden={workspaceRegionHidden(workspaceSection, "plan")}
        >
          <TaskSourcesPanel repoId={repoId} featurePlanId={workBriefId} />
        </aside>
        <aside
          className="wbg-region-document"
          data-testid="task-focus-document"
          hidden={workspaceRegionHidden(workspaceSection, "activity")}
        >
          {layout === "focus" ? (
            <button
              type="button"
              className="wbg-resize-handle"
              aria-label={`Resize ${TASK_LABEL} draft panel`}
              data-testid="task-document-resize"
              onPointerDown={startDocumentResize}
            />
          ) : null}
          <div ref={guidanceSectionRef}>
            <InitiativeGuidancePanel
              featurePlanId={workBriefId}
              refreshKey={guidanceRefreshKey}
              onSnapshot={setOrchestrationSnapshot}
            />
          </div>
          {layout === "focus" ? (
            <TaskDocumentPanel
              requestText={productText || featureRequestText || ""}
              promises={documentPromises}
              decisions={(resolvedQuestions || []).map((q) => ({
                question: q.text,
                answer: q.resolution?.answerText || q.resolution?.reason || q.resolution?.note || "",
                status: q.status,
              }))}
              openQuestions={documentQuestions}
              readinessLines={blockers}
              warningLines={warningLines}
              requirementBlock={requirementBlock}
              agreementStatus={agreementStatus}
            />
          ) : null}
        </aside>
        <div className="wbg-region-conversation" data-testid="task-focus-conversation">

      <aside
        className={`wbg-coach wbg-coach--${coach.role}`}
        data-testid="handoff-coach"
        data-coach-id={coach.id}
        data-coach-role={coach.role}
        hidden={
          workspaceRegionHidden(workspaceSection, "plan") &&
          workspaceRegionHidden(workspaceSection, "handoff")
        }
      >
        <div className="wbg-coach-top">
          <span className="wbg-coach-kicker">Handoff Coach</span>
          <span className="wbg-coach-role" data-testid="handoff-coach-role">
            {HANDOFF_COACH_ROLE_LABELS[coach.role] || "For requester"}
          </span>
        </div>
        <h4 className="wbg-coach-headline" data-testid="handoff-coach-headline">
          {coach.headline}
        </h4>
        <p className="wbg-coach-body" data-testid="handoff-coach-body">
          {coach.body}
        </p>
        {coach.understood ? (
          <p className="wbg-coach-understood" data-testid="handoff-coach-understood">
            <strong>What we understood:</strong> {coach.understood}
          </p>
        ) : null}
        {Array.isArray(coach.missing) && coach.missing.length > 0 ? (
          <div className="wbg-coach-missing" data-testid="handoff-coach-missing">
            <strong>What’s missing or risky:</strong>
            <ul>
              {coach.missing.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="wbg-coach-actions" data-testid="handoff-coach-actions">
          {coach.primaryAction ? (
            <button
              type="button"
              className="wbg-primary-button"
              data-testid="handoff-coach-primary"
              onClick={() => runCoachAction(coach.primaryAction.id)}
              disabled={coachPrimaryDisabled}
            >
              {coach.primaryAction.label}
            </button>
          ) : (
            <p className="wbg-hint" data-testid="handoff-coach-primary-none">
              No action needed right now — hang tight.
            </p>
          )}
          {(coach.secondaryActions || []).map((action) => (
            <button
              key={action.id}
              type="button"
              className="wbg-secondary-button"
              data-testid={`handoff-coach-secondary-${action.id}`}
              onClick={() => runCoachAction(action.id)}
              disabled={Boolean(busy) || readOnly}
            >
              {action.label}
            </button>
          ))}
        </div>
      </aside>

      {(planPreparing ||
        planInfo.key === "insufficient_evidence" ||
        planInfo.key === "failed" ||
        planInfo.key === "rejected") && (
        <section
          className={`wbg-card wbg-plan-status wbg-plan-status--${planInfo.key}`}
          data-testid="guided-plan-status"
          hidden={workspaceRegionHidden(workspaceSection, "plan")}
        >
          <h4 data-testid="guided-plan-status-title">{planInfo.title}</h4>
          <p className="wbg-hint" data-testid="guided-plan-status-summary">
            {planInfo.summary}
          </p>
          {planInfo.key === "insufficient_evidence" ? (
            <div className="wbg-actions-row" data-testid="guided-insufficient-actions">
              <button
                type="button"
                className="wbg-secondary-button"
                data-testid="guided-promise-draft-anyway"
                onClick={handlePrepareHandoff}
                disabled={Boolean(busy) || !repoId || extracting}
              >
                Create Task draft anyway
              </button>
              {typeof onRetryPlanning === "function" ? (
                <button
                  type="button"
                  className="wbg-secondary-button"
                  data-testid="guided-retry-planning"
                  onClick={onRetryPlanning}
                  disabled={Boolean(busy)}
                >
                  Retry planning
                </button>
              ) : null}
              <p className="wbg-draft-note">
                “Promise draft anyway” clarifies the agreement only — the engineering plan still needs
                a clearer request.
              </p>
            </div>
          ) : null}
          {(planInfo.recovery === "retry" || planInfo.key === "failed") &&
          typeof onRetryPlanning === "function" ? (
            <button
              type="button"
              className="wbg-secondary-button"
              data-testid="guided-retry-planning"
              onClick={onRetryPlanning}
              disabled={Boolean(busy) || planPreparing}
            >
              Retry planning
            </button>
          ) : null}
          <details className="wbg-tech-details" data-testid="guided-plan-tech">
            <summary>Technical details</summary>
            <pre>
              {JSON.stringify(
                {
                  status: planStatus,
                  error: planError,
                  badge: planInfo.badge,
                },
                null,
                2
              )}
            </pre>
          </details>
        </section>
      )}

      <section
        className="wbg-card"
        data-testid="guided-request-section"
        hidden={workspaceRegionHidden(workspaceSection, "plan")}
        ref={requestSectionRef}
      >
        {planStatus === "completed" || planStatus === "generating" || planStatus === "pending" ? (
          <>
            <h4>1. Turn the request into promises</h4>
            <p className="wbg-hint" data-testid="guided-request-explain">
              {busy === "extract" ? (
                <>
                  Understanding your request now — extracting promises from the{" "}
                  <strong>Original request</strong> and any reviewed screenshots.
                </>
              ) : draftView ? (
                <>
                  Promises below were extracted from the <strong>Original request</strong> and
                  reviewed screenshots. Use try again only if you want a fresh draft from the same
                  sources.
                </>
              ) : (
                <>
                  We’ll extract the commitments the engineer will accept from the request text and
                  any reviewed screenshots. Engineering guidance can keep preparing in the
                  background.
                </>
              )}
            </p>
            <details className="wbg-request-source" data-testid="guided-request-source">
              <summary>Show source text used for extraction</summary>
              <div className="wbg-request-display" data-testid="guided-request-text">
                {productText || featureRequestText || "No request text yet."}
              </div>
            </details>
          </>
        ) : (
          <>
            <h4>1. Describe what you want</h4>
            <p className="wbg-hint" data-testid="guided-request-explain">
              Write the product ask used to create this handoff. After planning, that text becomes the
              Original request and the source for promises.
            </p>
            <textarea
              className="wbg-textarea"
              data-testid="guided-request-input"
              rows={5}
              value={productText}
              onChange={(e) => setProductText(e.target.value)}
              placeholder="Describe the change you want…"
            />
          </>
        )}
        {busy === "extract" ? (
          <p className="wbg-draft-note" data-testid="guided-prepare-waiting">
            Preparing this Task…
          </p>
        ) : isApproved ? (
          <p className="wbg-draft-note" data-testid="guided-prepare-locked">
            Handoff already approved — revise below if you need changes.
          </p>
        ) : draftView ? (
          <button
            type="button"
            className="wbg-secondary-button"
            data-testid="guided-prepare-handoff"
            onClick={handlePrepareHandoff}
            disabled={Boolean(busy) || !repoId || readOnly}
          >
            Try again from request
          </button>
        ) : (
          <button
            type="button"
            className="wbg-primary-button"
            data-testid="guided-prepare-handoff"
            onClick={handlePrepareHandoff}
            disabled={Boolean(busy) || !repoId || readOnly}
          >
            Prepare handoff
          </button>
        )}
      </section>

      {(error || statusMessage) && (
        <div className="wbg-messages">
          {error ? (
            <div className="wbg-error" data-testid="guided-error" role="alert">
              <p>{error}</p>
              {(strictBlockedByQuestions || isBlockedOnlyByOpenQuestions(readiness)) &&
              !requirementBlock ? (
                <p data-testid="guided-approve-blocked-reason">
                  Approval stays blocked until remaining material decisions are answered.
                </p>
              ) : null}
              {errorDetails ? (
                <details className="wbg-tech-details">
                  <summary>Technical error details</summary>
                  <pre>{errorDetails}</pre>
                </details>
              ) : null}
            </div>
          ) : null}
          {statusMessage ? (
            <div className="wbg-status" data-testid="guided-status">
              {statusMessage}
            </div>
          ) : null}
        </div>
      )}

      <section
        className="wbg-card"
        data-testid="guided-promises-section"
        hidden={workspaceRegionHidden(workspaceSection, "plan")}
      >
        <h4>2. Promises</h4>
        <p className="wbg-hint">
          Each item below is one commitment the engineer will be asked to accept. Titles and delivery
          bullets are guidance for readability — the exact source sentence is what grounds the
          agreement. Remove or edit anything that should not be in this handoff.
        </p>
        {promisePresentations.showCorePromiseLayout ? (
          <p className="wbg-draft-note" data-testid="guided-core-promise-note">
            Core promise with a delivery breakdown. Edit or add promises if this does not match what
            you want delivered.
          </p>
        ) : null}
        {extracting ? (
          <p className="wbg-hint" data-testid="guided-promises-locked">
            Understanding your request…
          </p>
        ) : null}
        {!draftView ? (
          <div className="wbg-empty" data-testid="guided-no-draft">
            <p>
              {extracting
                ? "Extracting promises…"
                : planPreparing
                  ? "No Task draft yet. Engineering guidance is still preparing in the background."
                  : "No Task draft yet."}
            </p>
            {!extracting ? (
              <button
                type="button"
                className="wbg-secondary-button"
                onClick={handlePrepareHandoff}
                disabled={Boolean(busy) || !repoId}
                data-testid="guided-extract-promises"
              >
                {planInfo.key === "insufficient_evidence"
                  ? "Create Task draft anyway"
                  : "Extract promises from this request"}
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <p className="wbg-count" data-testid="guided-promises-count">
              {criteria.length} promise{criteria.length === 1 ? "" : "s"}
            </p>
            {criteria.length === 0 ? (
              <div
                className="wbg-empty wbg-empty--attention"
                data-testid="guided-no-promises"
              >
                <p className="wbg-attention">No promises found yet</p>
                <p className="wbg-hint">
                  A handoff needs at least one promise before it can be approved.
                  Add a promise from exact request wording, try again from the request, or make the
                  request more specific.
                </p>
                <div className="wbg-actions-row">
                  <button
                    type="button"
                    className="wbg-primary-button"
                    data-testid="guided-focus-add-promise"
                    onClick={focusAddPromise}
                    disabled={!canReviseTip || Boolean(busy)}
                  >
                    Add a promise
                  </button>
                  <button
                    type="button"
                    className="wbg-secondary-button"
                    data-testid="guided-try-again-from-request"
                    onClick={handlePrepareHandoff}
                    disabled={Boolean(busy) || !repoId || isApproved}
                  >
                    Try again from request
                  </button>
                  <button
                    type="button"
                    className="wbg-secondary-button"
                    data-testid="guided-make-request-specific"
                    onClick={focusRequest}
                    disabled={Boolean(busy)}
                  >
                    Make request more specific
                  </button>
                </div>
              </div>
            ) : (
              <ul className="wbg-promise-list" data-testid="guided-promises">
                {criteria.map((c, index) => {
                  const origin = originByCriterionId[c.criterionId];
                  const from = promiseSourceSentence(c, draftView);
                  const presentation = presentationById[c.criterionId] || null;
                  const title =
                    presentation?.title ||
                    (promisePresentations.showCorePromiseLayout
                      ? "Core promise"
                      : `Promise ${index + 1}`);
                  const sourceText = cleanFounderDisplayText(
                    presentation?.sourceText || from || promiseText(c)
                  );
                  const bullets = presentation?.deliveryBullets || [];
                  return (
                    <li
                      key={c.criterionId || promiseText(c)}
                      className="wbg-promise-card"
                      data-testid="guided-promise-card"
                    >
                      <p className="wbg-promise-title" data-testid="guided-promise-title">
                        {title}
                      </p>
                      <div className="wbg-promise-meta">
                        <span className="wbg-chip">On handoff</span>
                        {!isApproved ? (
                          <span className="wbg-chip wbg-chip-muted">Suggested until approved</span>
                        ) : null}
                      </div>
                      {bullets.length > 0 ? (
                        <div
                          className="wbg-delivery-breakdown"
                          data-testid="guided-delivery-breakdown"
                        >
                          <p className="wbg-hint">
                            <strong>Delivery breakdown</strong>{" "}
                            <span className="wbg-chip wbg-chip-muted">Guidance — not separate promises</span>
                          </p>
                          <ul className="wbg-bullet-list">
                            {bullets.map((b) => (
                              <li key={b}>{b}</li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <p className="wbg-promise-text">{promiseText(c)}</p>
                      )}
                      <p className="wbg-from" data-testid="guided-promise-source">
                        <strong>Exact source:</strong> {sourceText || "(source sentence unavailable)"}
                      </p>
                      <details className="wbg-tech-details" data-testid="guided-promise-tech">
                        <summary>Citation / technical details</summary>
                        <div className="wbg-promise-meta">
                          <span className="wbg-chip">{sourceTrustLabel(origin)}</span>
                          <span className="wbg-chip wbg-chip-muted">{originLabel(origin)}</span>
                        </div>
                        <pre>
                          {JSON.stringify(
                            {
                              criterionId: c.criterionId,
                              sourceClauseIds: c.sourceClauseIds,
                              actor: c.actor,
                              action: c.action,
                              expectedOutcomes: c.expectedOutcomes,
                            },
                            null,
                            2
                          )}
                        </pre>
                      </details>
                      {canReviseTip &&
                      editingPromiseId !== c.criterionId &&
                      markNotRequiredFor !== c.criterionId ? (
                        <div
                          className="wbg-question-actions"
                          data-testid={`guided-promise-actions-${c.criterionId}`}
                        >
                          <button
                            type="button"
                            className="wbg-btn"
                            data-testid={`guided-promise-edit-${c.criterionId}`}
                            disabled={Boolean(busy)}
                            onClick={() => {
                              setMarkNotRequiredFor(null);
                              setEditingPromiseId(c.criterionId);
                              setEditPromiseText(promiseText(c));
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="wbg-btn"
                            data-testid={`guided-promise-remove-${c.criterionId}`}
                            disabled={Boolean(busy)}
                            onClick={() => handleRemovePromise(c.criterionId)}
                          >
                            Remove from handoff
                          </button>
                          <button
                            type="button"
                            className="wbg-btn"
                            data-testid={`guided-promise-not-required-${c.criterionId}`}
                            disabled={Boolean(busy)}
                            onClick={() => {
                              setEditingPromiseId(null);
                              setMarkNotRequiredFor(c.criterionId);
                              setMarkNotRequiredReason("");
                            }}
                          >
                            Mark not required
                          </button>
                        </div>
                      ) : null}
                      {editingPromiseId === c.criterionId ? (
                        <div
                          className="wbg-add-form"
                          data-testid={`guided-promise-edit-form-${c.criterionId}`}
                        >
                          <label className="wbg-answer-label">
                            Edit promise wording
                            <textarea
                              className="wbg-textarea wbg-answer-input"
                              data-testid={`guided-promise-edit-text-${c.criterionId}`}
                              rows={3}
                              value={editPromiseText}
                              onChange={(e) => setEditPromiseText(e.target.value)}
                              placeholder="Write the commitment you want the engineer to accept…"
                            />
                          </label>
                          <p className="wbg-draft-note" data-testid="guided-promise-edit-warn">
                            Changing wording creates a new draft version. Keep it tied to what you
                            actually want — the saved text becomes the cited source for this promise.
                            Citation/readiness problems will block or warn before approval.
                            {isApproved
                              ? " This changes the agreement, so it needs approval again."
                              : ""}
                          </p>
                          <div className="wbg-actions-row">
                            <button
                              type="button"
                              className="wbg-btn wbg-btn-primary"
                              data-testid={`guided-promise-edit-save-${c.criterionId}`}
                              disabled={
                                Boolean(busy) || !String(editPromiseText || "").trim()
                              }
                              onClick={() => handleSaveEditedPromise(c.criterionId)}
                            >
                              {busy === "revise" ? "Saving…" : "Save promise"}
                            </button>
                            <button
                              type="button"
                              className="wbg-btn"
                              data-testid={`guided-promise-edit-cancel-${c.criterionId}`}
                              onClick={() => {
                                setEditingPromiseId(null);
                                setEditPromiseText("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : null}
                      {markNotRequiredFor === c.criterionId ? (
                        <div
                          className="wbg-add-form"
                          data-testid={`guided-promise-not-required-form-${c.criterionId}`}
                        >
                          <label className="wbg-answer-label">
                            Why is this not required?
                            <textarea
                              className="wbg-textarea wbg-answer-input"
                              rows={2}
                              value={markNotRequiredReason}
                              onChange={(e) => setMarkNotRequiredReason(e.target.value)}
                              placeholder="Short reason for the engineer…"
                            />
                          </label>
                          <div className="wbg-actions-row">
                            <button
                              type="button"
                              className="wbg-btn wbg-btn-primary"
                              data-testid={`guided-promise-not-required-save-${c.criterionId}`}
                              disabled={Boolean(busy) || !String(markNotRequiredReason || "").trim()}
                              onClick={() => handleMarkPromiseNotRequired(c.criterionId)}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="wbg-btn"
                              onClick={() => setMarkNotRequiredFor(null)}
                            >
                              Cancel
                            </button>
                          </div>
                          {isApproved ? (
                            <p className="wbg-draft-note" data-testid="guided-reapproval-note">
                              This changes the agreement, so it needs approval again.
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}

            <div
              className={`wbg-add-form${criteria.length === 0 ? " wbg-add-form--primary" : ""}`}
              data-testid="guided-add-promise"
              ref={addPromiseRef}
            >
              <label className="wbg-answer-label">
                {criteria.length === 0 ? "Add a promise" : "Add another promise"}
                <textarea
                  className="wbg-textarea wbg-answer-input"
                  data-testid="guided-new-promise-text"
                  rows={2}
                  value={newPromiseText}
                  onChange={(e) => setNewPromiseText(e.target.value)}
                  placeholder="Copy an exact sentence from your request, e.g. I want Fastify to stop them and send back a timeout error…"
                  disabled={!canReviseTip || Boolean(busy)}
                />
              </label>
              <button
                type="button"
                className={
                  criteria.length === 0 ? "wbg-primary-button" : "wbg-secondary-button"
                }
                data-testid="guided-add-promise-btn"
                onClick={handleAddPromise}
                disabled={
                  !canReviseTip ||
                  Boolean(busy) ||
                  !String(newPromiseText || "").trim()
                }
              >
                {busy === "revise" ? "Saving…" : "Add promise"}
              </button>
              {!canReviseTip ? (
                <p className="wbg-hint">
                  {extracting
                    ? "Wait until promise extraction finishes."
                    : "Prepare a draft first to change promises."}
                </p>
              ) : (
                <>
                  <p className="wbg-draft-note">
                    {isApproved
                      ? "Creates a new draft version — this changes the agreement, so it needs approval again."
                      : "Creates a new draft version. Your text is saved as the cited source for this promise."}
                  </p>
                  <p className="wbg-hint" data-testid="guided-add-promise-citation-note">
                    Prefer copying wording from the request. Manually added promises are allowed and
                    are cited to the text you type (not invented by the model).
                  </p>
                </>
              )}
            </div>

            {coverageSuggestions.length > 0 && canReviseTip ? (
              <div
                className="wbg-coverage-suggestions"
                data-testid="guided-coverage-suggestions"
              >
                <p className="wbg-hint">
                  <strong>More suggested promises</strong> — same card shape as above. Edit or remove
                  them here; add to handoff when you want them in the agreement (testing, scalability,
                  editability, notes).
                </p>
                <ul className="wbg-promise-list" data-testid="guided-suggested-promises">
                  {coverageSuggestions.map((s) => (
                    <li
                      key={s.id}
                      className="wbg-promise-card wbg-promise-card--suggested"
                      data-testid="guided-suggested-promise-card"
                      data-suggestion-id={s.id}
                    >
                      <p className="wbg-promise-title">{s.title}</p>
                      <div className="wbg-promise-meta">
                        <span className="wbg-chip">Suggested</span>
                        <span className="wbg-chip wbg-chip-muted">{s.area}</span>
                        <span className="wbg-chip wbg-chip-muted">Not on handoff yet</span>
                      </div>
                      {(s.deliveryBullets || []).length > 0 ? (
                        <div className="wbg-delivery-breakdown">
                          <p className="wbg-hint">
                            <strong>Delivery breakdown</strong>{" "}
                            <span className="wbg-chip wbg-chip-muted">
                              Guidance — not separate promises
                            </span>
                          </p>
                          <ul className="wbg-bullet-list">
                            {s.deliveryBullets.map((b) => (
                              <li key={b}>{b}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      <p className="wbg-from">
                        <strong>Exact source:</strong>{" "}
                        {cleanFounderDisplayText(s.sourceText)}
                      </p>
                      {editingSuggestionId === s.id ? (
                        <div className="wbg-add-form">
                          <label className="wbg-answer-label">
                            Edit suggested promise
                            <textarea
                              className="wbg-textarea wbg-answer-input"
                              data-testid={`guided-suggestion-edit-input-${s.id}`}
                              rows={3}
                              value={s.sourceText}
                              onChange={(e) =>
                                setSuggestionEdits((prev) => ({
                                  ...prev,
                                  [s.id]: e.target.value,
                                }))
                              }
                            />
                          </label>
                          <div className="wbg-question-actions">
                            <button
                              type="button"
                              className="wbg-btn wbg-btn-primary"
                              data-testid={`guided-suggestion-save-edit-${s.id}`}
                              onClick={() => setEditingSuggestionId(null)}
                            >
                              Done editing
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="wbg-question-actions">
                          <button
                            type="button"
                            className="wbg-btn"
                            data-testid={`guided-suggestion-edit-${s.id}`}
                            disabled={Boolean(busy)}
                            onClick={() => setEditingSuggestionId(s.id)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="wbg-btn"
                            data-testid={`guided-suggestion-remove-${s.id}`}
                            disabled={Boolean(busy)}
                            onClick={() => {
                              setDismissedSuggestionIds((prev) =>
                                prev.includes(s.id) ? prev : [...prev, s.id]
                              );
                              setEditingSuggestionId((cur) => (cur === s.id ? null : cur));
                            }}
                          >
                            Remove
                          </button>
                          <button
                            type="button"
                            className="wbg-btn wbg-btn-primary"
                            data-testid={`guided-suggestion-add-${s.id}`}
                            disabled={Boolean(busy)}
                            onClick={() => handleAddSuggestionToHandoff(s)}
                          >
                            {busy === "revise" ? "Adding…" : "Add to handoff"}
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </section>

      <section
        className="wbg-card"
        data-testid="guided-notes-section"
        hidden={workspaceRegionHidden(workspaceSection, "plan")}
      >
        <h4>Notes for engineer</h4>
        <p className="wbg-hint" data-testid="guided-notes-explain">
          These are included in what the engineer reviews before accepting. They are saved on the
          handoff version (not browser-only).
        </p>
        {humanNotes.length > 0 ? (
          <ul className="wbg-question-list" data-testid="guided-human-notes">
            {humanNotes.map((n) => {
              const clarification = parseClarificationNote(n.text);
              return (
                <li key={n.assumptionId} className="wbg-question-card">
                  {clarification ? (
                    <>
                      <p className="wbg-question-text">{clarification.question}</p>
                      <p className="wbg-from">
                        <strong>
                          {clarification.kind === "not_required" ? "Reason:" : "Answer:"}
                        </strong>{" "}
                        {clarification.response}
                      </p>
                    </>
                  ) : (
                    <p className="wbg-question-text">{n.text}</p>
                  )}
                  <span className="wbg-chip">Included in handoff</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="wbg-hint">No notes for the engineer yet.</p>
        )}
        <div className="wbg-add-form" data-testid="guided-add-note">
          <label className="wbg-answer-label">
            Add a note for the engineer
            <textarea
              className="wbg-textarea wbg-answer-input"
              data-testid="guided-new-note-text"
              rows={2}
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder="Example: Keep the public API surface stable for existing callers."
              disabled={!canReviseTip || Boolean(busy)}
            />
          </label>
          <button
            type="button"
            className="wbg-secondary-button"
            data-testid="guided-add-note-btn"
            onClick={handleAddNote}
            disabled={
              !canReviseTip || Boolean(busy) || !String(newNoteText || "").trim()
            }
          >
            {busy === "revise" ? "Saving…" : "Add note"}
          </button>
          {!canReviseTip ? (
            <p className="wbg-hint">
              {extracting
                ? "Wait until promise extraction finishes."
                : "Prepare a draft first to add notes."}
            </p>
          ) : (
            <p className="wbg-draft-note">
              {isApproved
                ? "Creates a new draft version — this changes the agreement, so it needs approval again."
                : "Saved on the handoff version the engineer will review."}
            </p>
          )}
        </div>
      </section>

      <section
        className="wbg-card"
        data-testid="guided-questions-section"
        ref={questionsSectionRef}
        hidden={workspaceRegionHidden(workspaceSection, "plan")}
      >
        <h4>3. Open questions</h4>
        <p className="wbg-hint" data-testid="guided-questions-note">
          Product clarifications run as a decision loop, not a fixed questionnaire. Answer the
          highest-value decision, then the handoff re-evaluates and advances to the next material
          gap. It stops when no remaining question can materially change implementation or acceptance
          and the draft passes its grounding checks.
        </p>
        {questionPartition.technicalOpenCount > 0 &&
        (strictBlockedByQuestions ||
          (lastPolicyChecked === "strict" && readiness?.approvalAllowed === false) ||
          (readiness?.policyMode === "strict" && readiness?.approvalAllowed === false)) ? (
          <div className="wbg-attention" data-testid="guided-technical-questions-strict-banner">
            Handoff is blocked by {questionPartition.technicalOpenCount} technical review question
            {questionPartition.technicalOpenCount === 1 ? "" : "s"}. View Technical details.
          </div>
        ) : null}
        {questionPartition.founderOpenCount > 0 ? (
          <p className="wbg-attention" data-testid="guided-questions-attention">
            {isApproved || approvedWithWarnings
              ? `${questionPartition.founderOpenCount} clarification${
                  questionPartition.founderOpenCount === 1 ? "" : "s"
                } still open on an approved tip`
              : `${questionPartition.founderOpenCount} clarification${
                  questionPartition.founderOpenCount === 1 ? "" : "s"
                } to review`}
          </p>
        ) : draftView ? (
          <p className="wbg-hint" data-testid="guided-questions-empty">
            {questionPartition.technicalOpenCount > 0
              ? "No product clarifications in the main list. Technical review questions are under Technical details."
              : "No open questions right now."}
          </p>
        ) : (
          <p className="wbg-hint">Questions appear after you prepare this Task.</p>
        )}

        <div data-testid="guided-questions">
          <ul className="wbg-question-list">
            {questionPartition.founderQuestions.slice(0, 1).map((q) => {
              const noteKey = q.resolvable ? q.questionId : q.guidanceKey;
              const resolving =
                q.resolvable && busy && String(busy).startsWith(`resolve:${q.questionId}:`);
              const categoryLabel =
                founderQuestionCategoryLabel(q.category) ||
                CATEGORY_LABELS[q.category] ||
                CATEGORY_LABELS.other;
              const statusLabel =
                q.status === "deferred" ? "Deferred (still open)" : "Needs clarification";
              return (
                <li
                  key={noteKey || q.displayText}
                  className="wbg-question-card"
                  data-testid="guided-question-card"
                  data-question-kind={q.displayKind}
                >
                  <p className="wbg-question-text">
                    {cleanFounderDisplayText(q.displayText)}
                  </p>
                  {q.whyItMatters ? (
                    <p className="wbg-question-why" data-testid="guided-question-why">
                      <strong>Why this matters:</strong> {q.whyItMatters}
                    </p>
                  ) : null}
                  {q.answerFormat ? (
                    <p className="wbg-question-answer-format" data-testid="guided-question-answer-format">
                      <strong>Answer with:</strong> {q.answerFormat}
                    </p>
                  ) : null}
                  <div className="wbg-promise-meta">
                    <span className="wbg-chip" data-testid="guided-next-question">
                      Ask now
                    </span>
                    <span className="wbg-chip">{statusLabel}</span>
                    <span className={`wbg-chip wbg-sev-${q.severity || "medium"}`}>
                      {SEVERITY_LABELS[q.severity] || "Medium"}
                    </span>
                    {categoryLabel && categoryLabel !== statusLabel ? (
                      <span className="wbg-chip wbg-chip-muted">{categoryLabel}</span>
                    ) : null}
                    {/-followup-/i.test(String(q.questionId || "")) ? (
                      <span className="wbg-chip" data-testid="guided-question-revealed">
                        Revealed by your last answer
                      </span>
                    ) : null}
                    {q.resolvable && isHumanAddedQuestionId(q.questionId) ? (
                      <span className="wbg-chip wbg-chip-muted">Offered by engineer</span>
                    ) : null}
                  </div>
                  <label className="wbg-answer-label">
                    Your answer
                    <textarea
                      className="wbg-textarea wbg-answer-input"
                      data-testid={`guided-question-note-${noteKey || "unknown"}`}
                      rows={2}
                      value={questionNotes[noteKey] || ""}
                      onChange={(e) => updateQuestionNote(noteKey, e.target.value)}
                      placeholder={`Write your answer, or use ${QUESTION_RECOMMEND}…`}
                      disabled={!canResolveQuestions || Boolean(busy)}
                    />
                  </label>
                  {suggestedExampleKeys[noteKey] ? (
                    <p
                      className="wbg-draft-note"
                      data-testid={`guided-question-suggestion-banner-${noteKey}`}
                    >
                      Recommended option filled in — edit freely. Nothing is decided until you
                      confirm.
                    </p>
                  ) : null}
                  {canResolveQuestions ? (
                    <div
                      className="wbg-question-actions"
                      data-testid={`guided-question-actions-${noteKey}`}
                    >
                      <button
                        type="button"
                        className="wbg-btn"
                        data-testid={`guided-question-suggest-${noteKey}`}
                        disabled={Boolean(busy)}
                        onClick={() => applySuggestedExample(noteKey, q.displayText)}
                      >
                        {QUESTION_RECOMMEND}
                      </button>
                      <button
                        type="button"
                        className="wbg-btn wbg-btn-primary"
                        data-testid={`guided-question-answer-${noteKey}`}
                        disabled={Boolean(busy)}
                        onClick={() =>
                          q.resolvable
                            ? handleResolveQuestion(q.questionId, "answer")
                            : handleGuidanceQuestionNote(q.guidanceKey, q.displayText, "answer")
                        }
                      >
                        {busy === `resolve:${q.questionId}:answer` ||
                        (busy === "revise" && questionNotes[noteKey])
                          ? "Saving…"
                          : QUESTION_SAVE_ANSWER}
                      </button>
                      <button
                        type="button"
                        className="wbg-btn"
                        data-testid={`guided-question-not-sure-${noteKey}`}
                        disabled={Boolean(busy)}
                        onClick={() =>
                          q.resolvable
                            ? handleResolveQuestion(q.questionId, "defer", {
                                note: String(questionNotes[noteKey] || "").trim() || "I'm not sure",
                              })
                            : handleGuidanceQuestionNote(q.guidanceKey, q.displayText, "defer")
                        }
                      >
                        {QUESTION_NOT_SURE}
                      </button>
                      <button
                        type="button"
                        className="wbg-btn"
                        data-testid={`guided-question-not-required-${noteKey}`}
                        disabled={Boolean(busy)}
                        onClick={() =>
                          q.resolvable
                            ? handleResolveQuestion(q.questionId, "mark_not_required")
                            : handleGuidanceQuestionNote(
                                q.guidanceKey,
                                q.displayText,
                                "mark_not_required"
                              )
                        }
                      >
                        {QUESTION_DOES_NOT_APPLY}
                      </button>
                      <button
                        type="button"
                        className="wbg-btn"
                        data-testid={`guided-question-defer-${noteKey}`}
                        disabled={Boolean(busy)}
                        onClick={() =>
                          q.resolvable
                            ? handleResolveQuestion(q.questionId, "defer")
                            : handleGuidanceQuestionNote(q.guidanceKey, q.displayText, "defer")
                        }
                      >
                        {QUESTION_DEFER}
                      </button>
                    </div>
                  ) : (
                    <p className="wbg-draft-note">Prepare this Task to answer questions.</p>
                  )}
                  {resolving ? null : (
                    <p className="wbg-draft-note">
                      {q.resolvable
                        ? "Saving updates this Task. You can change this decision afterward."
                        : "Saving keeps your answer visible below and adds it as a note for the engineer."}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          {questionPartition.founderQuestions.length > 1 ? (
            <details className="wbg-queued-questions" data-testid="guided-queued-questions">
              <summary>
                Later questions ({questionPartition.founderQuestions.length - 1})
              </summary>
              <ul className="wbg-question-list">
                {questionPartition.founderQuestions.slice(1).map((q) => (
                  <li
                    key={(q.resolvable ? q.questionId : q.guidanceKey) || q.displayText}
                    className="wbg-question-card wbg-question-card--queued"
                    data-testid="guided-queued-question-card"
                  >
                    <p className="wbg-question-text">{cleanFounderDisplayText(q.displayText)}</p>
                    {q.resolvable && isHumanAddedQuestionId(q.questionId) ? (
                      <span className="wbg-chip wbg-chip-muted">Offered by engineer</span>
                    ) : null}
                    {q.whyItMatters ? (
                      <p className="wbg-question-why">
                        <strong>Why this matters:</strong> {q.whyItMatters}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>

        <div className="wbg-add-form" data-testid="guided-offer-question">
          <label className="wbg-answer-label">
            Offer an open question
            <textarea
              className="wbg-textarea wbg-answer-input"
              data-testid="guided-new-question-text"
              rows={2}
              value={newQuestionText}
              onChange={(e) => setNewQuestionText(e.target.value)}
              placeholder="Example: Which routes should be excluded from the timeout?"
              disabled={!canReviseTip || Boolean(busy)}
            />
          </label>
          <button
            type="button"
            className="wbg-secondary-button"
            data-testid="guided-offer-question-btn"
            onClick={handleOfferOpenQuestion}
            disabled={
              !canReviseTip || Boolean(busy) || !String(newQuestionText || "").trim()
            }
          >
            {busy === "revise" ? "Adding…" : "Add open question"}
          </button>
          {!canReviseTip ? (
            <p className="wbg-hint">
              {extracting
                ? "Wait until promise extraction finishes."
                : "Prepare this Task first to offer questions."}
            </p>
          ) : (
            <p className="wbg-draft-note">
              Saves on the handoff as a real open question for product to answer. Can block Strict
              until answered or marked not required.
              {isApproved
                ? " This changes the agreement, so it needs approval again."
                : ""}
            </p>
          )}
        </div>

        {(questionPartition.savedGuidanceQuestions || []).length > 0 ? (
          <div
            className="wbg-saved-clarifications"
            data-testid="guided-saved-clarifications"
          >
            <h5>Saved clarifications</h5>
            <p className="wbg-hint">
              These stay on the handoff for the engineer. They do not clear Strict by themselves.
            </p>
            <ul className="wbg-question-list">
              {questionPartition.savedGuidanceQuestions.map((q) => (
                <li
                  key={q.guidanceKey || q.displayText}
                  className="wbg-question-card"
                  data-testid="guided-saved-clarification-card"
                >
                  <p className="wbg-question-text">{q.displayText}</p>
                  <div className="wbg-promise-meta">
                    <span className="wbg-chip">
                      {q.savedKind === "not_required" ? "Not required" : "Answered"}
                    </span>
                    <span className="wbg-chip wbg-chip-muted">Included for engineer</span>
                  </div>
                  <p className="wbg-from" data-testid="guided-saved-clarification-answer">
                    <strong>
                      {q.savedKind === "not_required" ? "Reason:" : "Your answer:"}
                    </strong>{" "}
                    {q.savedResponse}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {questionPartition.technicalOpenCount > 0 ? (
          <details
            className="wbg-tech-details"
            data-testid="guided-technical-questions"
          >
            <summary>
              Technical details ({questionPartition.technicalOpenCount} internal review question
              {questionPartition.technicalOpenCount === 1 ? "" : "s"})
            </summary>
            <p className="wbg-hint">
              Containment / extraction review notes. They can still affect Strict readiness until
              answered or marked not required on the server.
            </p>
            <ul className="wbg-question-list" data-testid="guided-technical-question-list">
              {questionPartition.technicalQuestions.map((q) => (
                <li
                  key={q.questionId || q.displayText}
                  className="wbg-question-card"
                  data-testid="guided-technical-question-card"
                >
                  <p className="wbg-question-text">{q.displayText || q.text}</p>
                  {canResolveQuestions ? (
                    <div className="wbg-question-actions">
                      <label className="wbg-answer-label">
                        Your answer
                        <textarea
                          className="wbg-textarea wbg-answer-input"
                          data-testid={`guided-tech-question-note-${q.questionId}`}
                          rows={2}
                          value={questionNotes[q.questionId] || ""}
                          onChange={(e) => updateQuestionNote(q.questionId, e.target.value)}
                          disabled={Boolean(busy)}
                        />
                      </label>
                      <button
                        type="button"
                        className="wbg-btn wbg-btn-primary"
                        data-testid={`guided-tech-question-answer-${q.questionId}`}
                        disabled={Boolean(busy)}
                        onClick={() => handleResolveQuestion(q.questionId, "answer")}
                      >
                        {QUESTION_SAVE_ANSWER}
                      </button>
                      <button
                        type="button"
                        className="wbg-btn"
                        data-testid={`guided-tech-question-not-required-${q.questionId}`}
                        disabled={Boolean(busy)}
                        onClick={() => handleResolveQuestion(q.questionId, "mark_not_required")}
                      >
                        {QUESTION_DOES_NOT_APPLY}
                      </button>
                      <button
                        type="button"
                        className="wbg-btn"
                        data-testid={`guided-tech-question-defer-${q.questionId}`}
                        disabled={Boolean(busy)}
                        onClick={() => handleResolveQuestion(q.questionId, "defer")}
                      >
                        {QUESTION_DEFER}
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </details>
        ) : null}

        {resolvedQuestions.length > 0 ? (
          <div className="wbg-resolved-questions" data-testid="guided-resolved-questions">
            <h5>Resolved on this version</h5>
            <ul className="wbg-question-list">
              {resolvedQuestions.map((q) => (
                <li
                  key={q.questionId || q.text}
                  className="wbg-question-card wbg-question-card--resolved"
                  data-testid="guided-resolved-question-card"
                >
                  <p className="wbg-question-text">{q.text}</p>
                  <div className="wbg-promise-meta">
                    <span className="wbg-chip wbg-chip-muted">
                      {q.status === "answered"
                        ? "Answered"
                        : q.status === "not_required"
                          ? "Does not apply"
                          : "Resolved in successor"}
                    </span>
                  </div>
                  {q.resolution?.answerText ? (
                    <p className="wbg-hint">Answer: {q.resolution.answerText}</p>
                  ) : null}
                  {q.resolution?.reason ? (
                    <p className="wbg-hint">Reason: {q.resolution.reason}</p>
                  ) : null}
                  {canResolveQuestions &&
                  (q.status === "answered" || q.status === "not_required") ? (
                    <div className="wbg-question-actions">
                      <label className="wbg-answer-label">
                        Revise this answer
                        <textarea
                          className="wbg-textarea wbg-answer-input"
                          data-testid={`guided-question-revise-note-${q.questionId}`}
                          rows={2}
                          value={questionNotes[q.questionId] || ""}
                          onChange={(e) => updateQuestionNote(q.questionId, e.target.value)}
                          placeholder="Type a new answer to replace the previous one"
                          disabled={Boolean(busy)}
                        />
                      </label>
                      <button
                        type="button"
                        className="wbg-btn"
                        data-testid={`guided-question-revise-${q.questionId}`}
                        disabled={Boolean(busy)}
                        onClick={() => handleResolveQuestion(q.questionId, "answer")}
                      >
                        {QUESTION_REVISE}
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <section
        className="wbg-card"
        data-testid="guided-handoff-section"
        ref={handoffSectionRef}
        hidden={workspaceRegionHidden(workspaceSection, "handoff")}
      >
        <h4>4. Handoff decision</h4>
        <p className="wbg-hint">
          Skim the promises below, then approve so the engineer can accept. Open questions can stay as
          warnings.
        </p>

        {extracting || !hasPersistedVersion ? (
          <p className="wbg-hint" data-testid="guided-handoff-locked">
            {extracting
              ? "Understanding your request…"
              : "Locked until this Task exists."}
          </p>
        ) : null}

        {hasEngineeringRejection ? (
          <div className="wbg-attention" data-testid="guided-engineer-rejection-banner">
            Engineer requested changes. Revise promises/notes/questions, then approve again.
          </div>
        ) : null}

        {hasPersistedVersion && hasPromises ? (
          <div
            className="wbg-agreement-snapshot"
            data-testid="guided-agreement-preview"
          >
            <div className="wbg-decision-header">
              <p className="wbg-agreement-heading" data-testid="guided-agreement-heading">
                What you are agreeing to
              </p>
              <p className="wbg-decision-count">
                {criteria.length} promise{criteria.length === 1 ? "" : "s"}
                {(questionPartition.founderQuestions || []).length > 0
                  ? ` · ${(questionPartition.founderQuestions || []).length} still open`
                  : ""}
              </p>
            </div>

            <div data-testid="guided-agreement-promises">
              <p className="wbg-decision-section-label">Promises (the contract)</p>
              <ol className="wbg-decision-promise-list">
                {criteria.map((c, index) => {
                  const presentation = presentationById[c.criterionId];
                  const title = presentation?.title || promiseText(c);
                  const sourceText = cleanFounderDisplayText(
                    presentation?.sourceText || promiseSourceSentence(c, draftView)
                  );
                  return (
                    <li
                      key={c.criterionId || title}
                      className="wbg-decision-promise"
                      data-testid="guided-agreement-promise-card"
                    >
                      <span className="wbg-decision-promise-index" aria-hidden="true">
                        {index + 1}
                      </span>
                      <div className="wbg-decision-promise-body">
                        <p className="wbg-decision-promise-title">{title}</p>
                        <p className="wbg-decision-promise-source">
                          {sourceText ? `“${sourceText}”` : "(source unavailable)"}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            {(() => {
              const shape = promisePresentations.deliveryShape || {};
              const openForAgreement = (questionPartition.founderQuestions || [])
                .map((q) => cleanFounderDisplayText(q.displayText))
                .filter(Boolean);
              const hasShape =
                (shape.backendBehavior || []).length ||
                (shape.userVisibleBehavior || []).length ||
                (shape.errorEdgeBehavior || []).length;
              if (!hasShape && openForAgreement.length === 0) return null;
              return (
                <details
                  className="wbg-decision-details"
                  data-testid="guided-delivery-shape-section"
                >
                  <summary data-testid="guided-delivery-shape-honesty">
                    Build guidance{" "}
                    <span className="wbg-decision-details-meta">
                      optional · not part of the contract
                    </span>
                  </summary>
                  <p className="wbg-hint">
                    Planning hints for how this might get built. They are not separate promises.
                  </p>
                  <dl className="wbg-dl" data-testid="guided-delivery-shape">
                    {(shape.backendBehavior || []).length > 0 ? (
                      <div>
                        <dt>Backend</dt>
                        <dd>
                          <ul className="wbg-bullet-list">
                            {shape.backendBehavior.map((b) => (
                              <li key={b}>{b}</li>
                            ))}
                          </ul>
                        </dd>
                      </div>
                    ) : null}
                    {(shape.userVisibleBehavior || []).length > 0 ? (
                      <div>
                        <dt>User-visible</dt>
                        <dd>
                          <ul className="wbg-bullet-list">
                            {shape.userVisibleBehavior.map((b) => (
                              <li key={b}>{b}</li>
                            ))}
                          </ul>
                        </dd>
                      </div>
                    ) : null}
                    {(shape.errorEdgeBehavior || []).length > 0 ? (
                      <div>
                        <dt>Errors / edges</dt>
                        <dd>
                          <ul className="wbg-bullet-list">
                            {shape.errorEdgeBehavior.map((b) => (
                              <li key={b}>{b}</li>
                            ))}
                          </ul>
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                  {openForAgreement.length > 0 ? (
                    <div className="wbg-decision-open-warn">
                      <p className="wbg-decision-section-label">Still open (warnings if you approve)</p>
                      <ul
                        className="wbg-bullet-list"
                        data-testid="guided-delivery-shape-questions"
                      >
                        {openForAgreement.map((q) => (
                          <li key={q}>{q}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </details>
              );
            })()}

            {(() => {
              const clarificationRows = [];
              if (humanNotes.length > 0) {
                for (const n of humanNotes) {
                  const clarification = parseClarificationNote(n.text);
                  if (clarification) {
                    clarificationRows.push({
                      key: n.assumptionId,
                      question: cleanFounderDisplayText(clarification.question),
                      answer: cleanFounderDisplayText(clarification.response),
                      kind: clarification.kind,
                    });
                  } else {
                    clarificationRows.push({
                      key: n.assumptionId,
                      question: null,
                      answer: cleanFounderDisplayText(n.text),
                      kind: "note",
                    });
                  }
                }
              } else {
                for (const q of questionPartition.savedGuidanceQuestions || []) {
                  clarificationRows.push({
                    key: q.guidanceKey,
                    question: cleanFounderDisplayText(q.displayText),
                    answer: cleanFounderDisplayText(q.savedResponse),
                    kind: q.savedKind === "not_required" ? "not_required" : "answered",
                  });
                }
              }
              if (clarificationRows.length === 0) return null;
              return (
                <div
                  className="wbg-decision-clarifications"
                  data-testid="guided-handoff-notes-snapshot"
                >
                  <p className="wbg-decision-section-label">
                    Clarifications for the engineer
                  </p>
                  <ul className="wbg-decision-qa-list">
                    {clarificationRows.map((row) => (
                      <li key={row.key} className="wbg-decision-qa">
                        {row.question ? (
                          <>
                            <p className="wbg-decision-qa-q">{row.question}</p>
                            <p className="wbg-decision-qa-a">
                              <span className="wbg-decision-qa-label">
                                {row.kind === "not_required" ? "Not required" : "Answer"}
                              </span>
                              {row.answer}
                            </p>
                          </>
                        ) : (
                          <p className="wbg-decision-qa-a">{row.answer}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>
        ) : null}

        {approvalLockedNoPromises ? (
          <div
            className="wbg-attention"
            data-testid="guided-handoff-needs-promises"
          >
            Add at least one promise before you can approve.
          </div>
        ) : null}

        {isApproved && !hasEngineeringRejection ? (
          <div className="wbg-status" data-testid="guided-approved-banner">
            {approvedWithWarnings || openQuestions.length > 0
              ? "Approved with warnings. Next: engineer accepts or requests changes."
              : "Approved. Next: engineer accepts or requests changes."}
          </div>
        ) : (
          <div className="wbg-decision-approve" data-testid="guided-readiness-summary">
            <div className="wbg-decision-approve-copy">
              <p className="wbg-agreement-heading">Approve this handoff?</p>
              <p className="wbg-hint" data-testid="guided-can-approve">
                {extracting || !hasPersistedVersion
                  ? PREPARE_TASK_FIRST
                  : approvalLockedNoPromises
                    ? "Add at least one promise first."
                    : // Ahead of the local readings: the server has recorded that these
                      // requirements cannot all hold, and it will refuse the approval.
                      // "Looks clear" would be the one sentence on this screen the
                      // customer could most reasonably act on and most regret.
                      requirementBlock
                      ? "These requirements conflict with each other. Settle the conflict before approving — see “Where this stands” above."
                      : readiness?.policyMode === "strict" &&
                          !customerReadiness.intentFreezeReady
                        ? "Freeze is blocked until remaining decisions are answered, deferred with an owner, or marked not required."
                        : customerReadiness.intentFreezeReady
                          ? "Ready to freeze — remaining open items must already be allowed by Strict."
                          : (questionPartition.founderQuestions || []).length > 0 ||
                              warningLines.length > 0
                            ? "You can approve now; remaining open items become warnings for the engineer."
                            : "Looks clear — approve to send it to the engineer."}
              </p>
            </div>
            {readiness?.policyMode === "strict" && readiness.approvalAllowed === false ? (
              <div data-testid="guided-strict-blocked" className="wbg-decision-strict-note">
                <p>
                  <strong>Strict mode is blocked</strong> until remaining questions are answered or
                  marked not required.
                </p>
                {blockers.length > 0 ? (
                  <ul>
                    {blockers.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            {(warningLines.length > 0 || approvalLockedNoPromises) &&
            readiness?.policyMode !== "strict" ? (
              <details className="wbg-decision-details" data-testid="guided-warnings">
                <summary>Warnings before approve</summary>
                <ul>
                  {approvalLockedNoPromises &&
                  !warningLines.some((w) => /at least one promise/i.test(w)) ? (
                    <li>Add at least one promise before you can approve.</li>
                  ) : null}
                  {warningLines.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        )}

        <div className="wbg-actions-row wbg-decision-actions">
          <button
            type="button"
            className="wbg-primary-button"
            data-testid="guided-approve-with-warnings"
            onClick={handleApproveAdvisory}
            disabled={
              readOnly ||
              Boolean(busy) ||
              extracting ||
              !hasPersistedVersion ||
              approvalLockedNoPromises ||
              Boolean(requirementBlock) ||
              hasEngAck ||
              (isApproved && !hasEngineeringRejection) ||
              (hasEngineeringRejection && contractState === "product_approved") ||
              (readiness?.policyMode === "strict" && !customerReadiness.intentFreezeReady)
            }
          >
            {busy === "approve"
              ? "Approving…"
              : isApproved && !hasEngineeringRejection
                ? "Approved"
                : hasEngineeringRejection && contractState === "product_approved"
                  ? "Revise first, then approve"
                  : hasEngineeringRejection
                    ? "Re-approve after changes"
                    : approvalLockedNoPromises
                      ? "Add a promise first"
                      : "Approve handoff"}
          </button>
        </div>

        {/* Suppressed rather than disabled while the requirements conflict: the whole
            offer is "you can still approve with warnings", and that is not true. */}
        {strictBlockedByQuestions && !isApproved && hasPromises && !requirementBlock ? (
          <div className="wbg-callout" data-testid="guided-strict-offer-advisory">
            <p>Approval is blocked because material decisions remain. Answer them before handing off.</p>
          </div>
        ) : null}

        <details className="wbg-tech-details" data-testid="guided-readiness-tech">
          <summary>Technical readiness details</summary>
          <pre>
            {JSON.stringify(readiness || { note: "No readiness report yet" }, null, 2)}
          </pre>
        </details>
      </section>

      <section
        className="wbg-card"
        data-testid="guided-delivery-section"
        ref={deliverySectionRef}
        hidden={workspaceRegionHidden(workspaceSection, "handoff")}
      >
        <h4>5. Engineer decision</h4>
        {!isApproved && !hasEngineeringRejection ? (
          <p className="wbg-hint" data-testid="guided-delivery-locked">
            Locked until product approval.
          </p>
        ) : hasEngineeringRejection ? (
          <div data-testid="guided-engineer-requested-changes">
            <p className="wbg-attention">Engineer requested changes</p>
            <p className="wbg-hint">
              Reason:{" "}
              {ENGINEERING_REJECTION_REASON_CODES.find(
                (r) => r.code === contract?.engineeringRejection?.reasonCode
              )?.label || contract?.engineeringRejection?.reasonCode}
            </p>
            <p className="wbg-question-text">{contract?.engineeringRejection?.comment}</p>
            <p className="wbg-draft-note">
              Revise the handoff above, then re-approve. Acknowledgment stays locked until then.
            </p>
          </div>
        ) : (
          <p className="wbg-next-step" data-testid="guided-delivery-next">
            Review the approved promises and notes below. Accept if you can build this, or request
            changes if the agreement needs work.
          </p>
        )}

        {(isApproved || hasEngineeringRejection) && draftView ? (
          <div
            className="wbg-agreement-snapshot"
            data-testid="guided-engineer-agreement-snapshot"
          >
            <p className="wbg-agreement-heading" data-testid="guided-engineer-accepting-heading">
              What you are accepting
            </p>
            <dl className="wbg-dl" data-testid="guided-engineer-agreement-meta">
              <div>
                <dt>Version / status</dt>
                <dd data-testid="guided-engineer-version-status">
                  v{contract?.version ?? "—"} · {contractState || "unknown"}
                </dd>
              </div>
              <div>
                <dt>Approval policy</dt>
                <dd data-testid="guided-engineer-approval-policy">
                  {approvalPolicyLabel || "—"}
                </dd>
              </div>
            </dl>

            <div data-testid="guided-engineer-promises-snapshot">
              <p className="wbg-hint">
                <strong>Source-backed promises</strong>
              </p>
              {criteria.length > 0 ? (
                <ul className="wbg-promise-list">
                  {criteria.map((c) => {
                    const presentation = presentationById[c.criterionId];
                    return (
                      <li
                        key={c.criterionId || promiseText(c)}
                        className="wbg-promise-card"
                        data-testid="guided-engineer-promise-card"
                      >
                        <p className="wbg-promise-title">
                          {presentation?.title || promiseText(c)}
                        </p>
                        {(presentation?.deliveryBullets || []).length > 0 ? (
                          <div data-testid="guided-engineer-delivery-breakdown">
                            <p className="wbg-hint">
                              <strong>Delivery breakdown</strong>{" "}
                              <span className="wbg-chip wbg-chip-muted">Guidance</span>
                            </p>
                            <ul className="wbg-bullet-list">
                              {presentation.deliveryBullets.map((b) => (
                                <li key={b}>{b}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        <p className="wbg-from">
                          <strong>Exact source:</strong>{" "}
                          {cleanFounderDisplayText(
                            presentation?.sourceText || promiseText(c)
                          )}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="wbg-attention">No promises on this approved tip.</p>
              )}
            </div>

            <div data-testid="guided-engineer-notes-snapshot">
              <p className="wbg-hint">
                <strong>Notes for engineer</strong>
              </p>
              {humanNotes.length > 0 ? (
                <ul className="wbg-question-list">
                  {humanNotes.map((n) => (
                    <li key={n.assumptionId} className="wbg-question-card">
                      <p className="wbg-question-text">{n.text}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="wbg-hint">No notes for the engineer.</p>
              )}
            </div>

            {(approvedWithWarnings || openQuestions.length > 0) && (
              <div data-testid="guided-engineer-warnings-snapshot">
                <p className="wbg-hint">
                  <strong>Open warnings / questions accepted under Advisory</strong>
                </p>
                {openQuestions.length > 0 ? (
                  <ul className="wbg-question-list">
                    {openQuestions.map((q) => (
                      <li key={q.questionId} className="wbg-question-card">
                        <p className="wbg-question-text">{q.text}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="wbg-hint">Approved with warnings (see readiness history).</p>
                )}
              </div>
            )}
          </div>
        ) : null}

        <dl className="wbg-dl">
          <div>
            <dt>Delivery branch</dt>
            <dd data-testid="guided-delivery-branch">
              {deliveryBranch ||
                (hasEngAck
                  ? "Select the delivered branch in Review delivered branch below."
                  : "Visible after the engineer accepts — not the next step yet.")}
            </dd>
          </div>
          <div>
            <dt>Engineer decision</dt>
            <dd data-testid="guided-eng-ack-status">
              {hasEngAck
                ? "Accepted"
                : hasEngineeringRejection
                  ? "Requested changes"
                  : isApproved
                    ? "Ready for engineer decision"
                    : "Locked until handoff approved"}
            </dd>
          </div>
        </dl>

        {engineerStartPack?.implementationPrompt ? (
          <div className="wbg-start-pack" data-testid="engineer-start-pack">
            <p className="wbg-agreement-heading">Engineer Start Pack</p>
            <p className="wbg-hint" data-testid="engineer-start-pack-hash">
              Bound to frozen version fingerprint {String(engineerStartPack.contentHash || "").slice(0, 12)}.
            </p>
            <button
              type="button"
              className="wbg-secondary-button"
              data-testid="engineer-start-pack-copy"
              onClick={() => {
                if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(engineerStartPack.implementationPrompt);
                }
              }}
            >
              Copy implementation prompt
            </button>
          </div>
        ) : isApproved ? (
          <p className="wbg-hint" data-testid="engineer-start-pack-missing">
            The implementation prompt is not on this Task yet. Refresh after freeze if it does not appear.
          </p>
        ) : null}

        <div className="wbg-actions-row">
          <button
            type="button"
            className="wbg-primary-button"
            data-testid="guided-eng-ack"
            onClick={handleAck}
            disabled={
              Boolean(busy) ||
              extracting ||
              !hasPersistedVersion ||
              contractState !== "product_approved" ||
              // A conflict recorded after product approved: the engineer is the last
              // person who should be committing to build it.
              Boolean(requirementBlock) ||
              hasEngAck ||
              hasEngineeringRejection
            }
          >
            {busy === "ack"
              ? "Saving…"
              : hasEngAck
                ? "Engineer accepted handoff"
                : "Engineer accepts handoff"}
          </button>
          <button
            type="button"
            className="wbg-secondary-button"
            data-testid="guided-eng-request-changes"
            onClick={() => setShowRejectForm(true)}
            disabled={
              Boolean(busy) ||
              extracting ||
              !hasPersistedVersion ||
              contractState !== "product_approved" ||
              hasEngAck ||
              hasEngineeringRejection
            }
          >
            Request changes
          </button>
        </div>

        {showRejectForm && !hasEngineeringRejection ? (
          <div className="wbg-add-form" data-testid="guided-reject-form">
            <p className="wbg-hint" data-testid="guided-reject-prompt">
              What part of the handoff needs to change? Review the approved promises and notes
              above, then choose a reason and comment.
            </p>
            <label className="wbg-answer-label">
              Reason
              <select
                className="wbg-textarea"
                data-testid="guided-reject-reason"
                value={rejectReasonCode}
                onChange={(e) => setRejectReasonCode(e.target.value)}
              >
                {ENGINEERING_REJECTION_REASON_CODES.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="wbg-answer-label">
              Comment
              <textarea
                className="wbg-textarea wbg-answer-input"
                data-testid="guided-reject-comment"
                rows={3}
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="What should change before you can accept this handoff?"
              />
            </label>
            <div className="wbg-actions-row">
              <button
                type="button"
                className="wbg-primary-button"
                data-testid="guided-reject-submit"
                onClick={handleRequestChanges}
                disabled={
                  Boolean(busy) ||
                  !String(rejectReasonCode || "").trim() ||
                  !String(rejectComment || "").trim()
                }
              >
                {busy === "reject" ? "Saving…" : "Send change request"}
              </button>
              <button
                type="button"
                className="wbg-secondary-button"
                onClick={() => setShowRejectForm(false)}
                disabled={Boolean(busy)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <section
        className="wbg-card wbg-acceptance-wrap"
        data-testid="guided-acceptance-section"
        ref={acceptanceSectionRef}
        hidden={workspaceRegionHidden(workspaceSection, "review")}
      >
        <h4>Delivery Review</h4>
        <p className="wbg-hint" data-testid="guided-acceptance-honesty">
          This compares the delivered work to the agreed {TASK_LABEL} requirements. A person still
          records the final decision.
        </p>
        {!hasEngAck ? (
          <p className="wbg-hint" data-testid="guided-acceptance-locked">
            Locked until the engineer accepts the handoff and a delivery branch is selected.
          </p>
        ) : !hasAcceptanceCriteria ? (
          <p className="wbg-attention" data-testid="guided-acceptance-no-criteria">
            This {TASK_LABEL} has no acceptance criteria to review against
            {planInfo.key === "insufficient_evidence"
              ? " (planning did not produce a confident criteria set)."
              : "."}{" "}
            Improve the request / plan before treating review as meaningful.
          </p>
        ) : null}
        <AcceptanceReviewSection
          featurePlanId={workBriefId}
          planStatus={planStatus}
          planTitle={featureRequestText}
          repoId={repoId}
          reviewEligible={hasEngAck}
          onLatestReviewChange={onLatestReviewChange}
          heading="Delivery Review"
          intro={`Compares the delivered revision to the frozen ${TASK_LABEL}. A person still records the decision.`}
        />
      </section>

      <div
        className={`wbg-agreement-status wbg-agreement-status--${
          agreementStatus.ready ? "ready" : "not-ready"
        }`}
        hidden={workspaceRegionHidden(workspaceSection, "handoff")}
        data-testid="guided-agreement-status"
        data-ready={agreementStatus.ready ? "true" : "false"}
        data-status-key={agreementStatus.key}
      >
        <div className="wbg-agreement-status-top">
          <span className="wbg-agreement-status-label" data-testid="guided-agreement-status-label">
            {agreementStatus.label}
          </span>
          <span className="wbg-chip wbg-chip-muted">
            Ready only after product approves and the engineer accepts
          </span>
        </div>
        <p className="wbg-agreement-status-headline" data-testid="guided-agreement-status-headline">
          {agreementStatus.headline}
        </p>
        <p className="wbg-hint" data-testid="guided-agreement-status-detail">
          {agreementStatus.detail}
        </p>
        {!agreementStatus.ready ? (
          <p className="wbg-draft-note">
            Draft promises stay editable for both parties until agreement. Full technical dump and
            “ready for delivery” wait until both sides accept.
          </p>
        ) : (
          <p className="wbg-draft-note">
            Agreed handoff is ready for delivery work. Revising promises later requires approval again.
          </p>
        )}
      </div>

      <details
        className="wbg-tech-details wbg-tech-panel"
        data-testid="guided-ic-technical"
        hidden={workspaceRegionHidden(workspaceSection, "activity")}
      >
        <summary>
          {agreementStatus.ready
            ? "Agreed handoff technical details"
            : "Promise draft technical details (not agreed yet)"}
        </summary>
        {agreementStatus.ready ? (
          <div>
            <p className="wbg-hint">
              Build Prompt loads this tip Intent Contract as the authoritative handoff (promises +
              clarifications), then appends {TASK_LABEL} plan steps and file targets.
            </p>
            <p className="wbg-decision-section-label">Promises on tip</p>
            <ol className="wbg-decision-promise-list">
              {handoffDeliverySummary.promises.map((p, i) => (
                <li key={`${i}-${p}`} className="wbg-decision-promise">
                  <span className="wbg-decision-promise-index" aria-hidden="true">
                    {i + 1}
                  </span>
                  <div className="wbg-decision-promise-body">
                    <p className="wbg-decision-promise-source">{p}</p>
                  </div>
                </li>
              ))}
            </ol>
            {handoffDeliverySummary.clarifications.length > 0 ? (
              <>
                <p className="wbg-decision-section-label">Clarifications</p>
                <ul className="wbg-decision-qa-list">
                  {handoffDeliverySummary.clarifications.map((c) => (
                    <li key={`${c.question}:${c.answer}`} className="wbg-decision-qa">
                      <p className="wbg-decision-qa-q">{c.question}</p>
                      <p className="wbg-decision-qa-a">
                        <span className="wbg-decision-qa-label">
                          {c.kind === "not_required" ? "Not required" : "Answer"}
                        </span>
                        {c.answer}
                      </p>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            <details className="wbg-tech-details">
              <summary>Raw tip JSON</summary>
              <pre>
                {JSON.stringify(
                  {
                    contractId,
                    versionId,
                    state: contractState,
                    coverageSummary,
                    draft: draftView,
                  },
                  null,
                  2
                )}
              </pre>
            </details>
          </div>
        ) : (
          <div data-testid="guided-ic-technical-gated">
            <p className="wbg-hint">
              Full promise dump stays collapsed until both parties agree. Summary only:
            </p>
            <pre>
              {JSON.stringify(
                {
                  contractId,
                  versionId,
                  state: contractState,
                  promiseCount: criteria.length,
                  openQuestionCount: openQuestions.length,
                  agreement: agreementStatus.key,
                  note: "Full draft JSON available after engineer acceptance.",
                },
                null,
                2
              )}
            </pre>
          </div>
        )}
      </details>
        </div>
      </div>
    </section>
  );
}

export default WorkBriefGuidedFlow;
